import Module, { registerModuleClass } from '@sparta.global.utilities/sparta-module-utility/dist/es6.js';
import SecureLinkUtility from '@sparta.sales.utilities/secure-link-utility/dist/es6.js';
import targetingUtility from '@sparta.sales.utilities/targeting-utility/dist/es6.js';
import taggingUtility from '@sparta.sales.utilities/tagging-utility/dist/es6.js';
import animationUtility from '@sparta.sales.utilities/animation-utility/dist/es6.js';
import modalUtility from '@sparta.sales.utilities/modal-utility/dist/es6.js';
import Handlebars from '@sparta.global.vendors/handlebars';
import spartaEventsUtility from '@sparta.global.utilities/sparta-events-utility/dist/es6.js';
import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import _ from '@sparta.global.vendors/lodash';
import { assetUrl, barker } from '@sparta.credit-cards.helpers/credit-cards/dist/es6.js';
import { json } from '@sparta.global.helpers/json-helper/dist/es6.js';
import { layerContent } from '@sparta.global.helpers/sparta-helper/dist/es6.js';
import { cinemaHelper } from '@sparta.global.helpers/cinema-helper/dist/es6.js';
import spartaMediaQueryUtility from '@sparta.global.utilities/sparta-mediaquery-utility/dist/es6.js';
import { button } from '@sparta.global.helpers/button-helper/dist/es6.js';
import { compare } from '@sparta.global.helpers/comparisons-helper/dist/es6.js';
import { picture } from '@sparta.global.helpers/picture-helper/dist/es6.js';

const SELECTORS$2 = {
  MASTHEAD_PAUSE_BUTTON: 'mastheadPauseButton',
  MASTHEAD_PLAY_BUTTON: 'mastheadPlayButton'
};

/**
 * AbstractAnimation class
 *
 * @class AbstractAnimation
 * @abstract
 */
class AbstractAnimation {
  /**
   * Constructor.
   */
  constructor() {
    // Cache dom elements already rendered.
    this.pauseButton = document.getElementById(SELECTORS$2.MASTHEAD_PAUSE_BUTTON);
    this.playButton = document.getElementById(SELECTORS$2.MASTHEAD_PLAY_BUTTON);
  }

  /**
   * Hides play and pause buttons.
   *
   * @returns {void}
   */
  hideAllButtons() {
    this.pauseButton.classList.add('hide');
    this.playButton.classList.add('hide');
  }

  /**
   * Shows the pause button.
   *
   * @returns {void}
   */
  showPauseButton() {
    this.hideAllButtons();
    this.pauseButton.classList.remove('hide');
  }

  /**
   * Shows the play button.
   *
   * @returns {void}
   */
  showPlayButton() {
    this.hideAllButtons();
    this.playButton.classList.remove('hide');
  }
}

const SELECTORS$1 = {
  BACKGROUND_VIDEO_MASTHEAD: 'backgroundVideoMasthead',
  VIDEO_PLACEHOLDER: 'video-placeholder',
  WEBM_SOURCE: '.webm-source',
  MP4_SOURCE: '.mp4-source',
  JCR_VIDEO: '#jcr-video-data'
};

/**
 * VideoBackground class
 *
 * @class VideoBackground
 */
class VideoBackground extends AbstractAnimation {
  /**
   * Get the current viewport.
   *
   * @readonly
   */
  get currentViewport() {
    return this.module.Store.getData('currentMQ').viewport;
  }

  /**
   * Constructor.
   *
   * @param {mastheadContainerModuleJS} module instance
   * @param {Object} videoData video data
   * @returns {VideoBackground} Instance
   */
  constructor(module, videoData) {
    super();
    this.module = module;
    this.videoData = videoData;

    // Cache client side rendered dom elements.
    this.videoEl = document.getElementById(SELECTORS$1.BACKGROUND_VIDEO_MASTHEAD);
    this.videoPlaceholder = document.getElementById(SELECTORS$1.VIDEO_PLACEHOLDER);
    this.webmSource = this.videoEl && this.videoEl.querySelector(SELECTORS$1.WEBM_SOURCE);
    this.mp4Source = this.videoEl && this.videoEl.querySelector(SELECTORS$1.MP4_SOURCE);
    try {
      this.videoData = JSON.parse(document.querySelector(SELECTORS$1.JCR_VIDEO).dataset.jcr);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Initializes the video background.
   *
   * @returns {void}
   */
  init() {
    this.bindResizeEvents();
    this.bindVideoEvents();
  }

  /**
   * Bind resize events
   *
   * @returns {void}
   */
  bindResizeEvents() {
    if (!this.videoData.videoInterchange) {
      // Only one video
      if (this.videoData.default) {
        this.videoData.default.forEach(sourceType => {
          if (sourceType.type === 'video/webm') {
            this.webmSource.src = sourceType.src;
          }
          if (sourceType.type === 'video/mp4') {
            this.mp4Source.src = sourceType.src;
          }
        });
        this.loadVideo();
      }
    } else {
      this.selectSourceForViewport(this.currentViewport);
      this.module.Events.sub(spartaMediaQueryUtility.EVENTS.BREAKPOINT_CHANGE, ({
        data
      }) => {
        this.selectSourceForViewport(data.viewport);
      });
    }
  }

  /**
   * Bind play / pause / end events.
   *
   * @returns {void}
   */
  bindVideoEvents() {
    this.videoEl.addEventListener('play', () => {
      if (this.hasVideoSource()) {
        this.showPauseButton();
      } else {
        this.hideAllButtons();
      }
    });
    this.videoEl.addEventListener('pause', () => {
      if (this.hasVideoSource()) {
        this.showPlayButton();
      } else {
        this.hideAllButtons();
      }
    });
    this.videoEl.addEventListener('ended', () => {
      if (!this.videoEl.paused) {
        this.videoEl.pause();
      }
    });
    this.pauseButton.addEventListener('click', () => {
      this.videoEl.pause();

      // In click handler to prevent focus on pageload.
      setTimeout(this.playButton.focus.bind(this.playButton), 200);
    });
    this.playButton.addEventListener('click', () => {
      this.videoEl.play();

      // In click handler to prevent focus on pageload.
      setTimeout(this.pauseButton.focus.bind(this.pauseButton), 200);
    });
  }

  /**
   * Select source for viewport.
   *
   * @param {String} viewport Viewport when resized and changed.
   * @returns {void}
   */
  selectSourceForViewport(viewport) {
    switch (true) {
      case viewport.indexOf('large') !== -1:
        this.loadSrc('large');
        break;
      case viewport.indexOf('medium') !== -1:
        this.loadSrc('medium');
        break;
      case viewport.indexOf('small') !== -1:
      default:
        this.loadSrc('small');
    }
  }

  /**
   * Loads video source.
   *
   * @param {String} viewport Viewport when resized and changed.
   * @returns {void}
   */
  loadSrc(viewport) {
    if (this.videoData.videoInterchange[viewport]) {
      this.videoData.videoInterchange[viewport].forEach(source => {
        if (source.type === 'video/webm') {
          this.webmSource.src = source.src;
        }
        if (source.type === 'video/mp4') {
          this.mp4Source.src = source.src;
        }
      });
    } else {
      this.webmSource.removeAttribute('src');
      this.mp4Source.removeAttribute('src');
      this.hideAllButtons();
    }
    this.loadVideo();
  }

  /**
   * Loads video.
   *
   * @returns {void}
   */
  loadVideo() {
    this.videoEl.oncanplaythrough = () => {
      this.videoEl.classList.remove('hide');
      if (this.videoPlaceholder) {
        this.videoPlaceholder.classList.add('hide');
      }
    };
    this.videoEl.classList.add('hide');
    if (this.videoPlaceholder) {
      this.videoPlaceholder.classList.remove('hide');
    }
    this.videoEl.load();
  }

  /**
   * Has video source.
   *
   * @returns {Boolean} has video source
   */
  hasVideoSource() {
    return this.webmSource.src || this.mp4Source.src;
  }
}

const Template$a = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <div id='masthead-default' data-ad-container=\""
    + ((stack1 = container.lambda(((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"module"))) && lookupProperty(stack1,"adContainer")), depth0)) != null ? stack1 : "")
    + "\" data-default-content-key=\""
    + ((stack1 = container.lambda(((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"module"))) && lookupProperty(stack1,"defaultContentKey")), depth0)) != null ? stack1 : "")
    + "\" data-jcr=\""
    + container.escapeExpression((lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"mockResponse") : depth0),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":113,"column":156},"end":{"line":113,"column":177}}}))
    + "\"></div>\r\n";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <div id='masthead-default' data-ad-container=\""
    + ((stack1 = container.lambda(((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"module"))) && lookupProperty(stack1,"adContainer")), depth0)) != null ? stack1 : "")
    + "\" data-default-content-key=\""
    + ((stack1 = container.lambda(((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"module"))) && lookupProperty(stack1,"defaultContentKey")), depth0)) != null ? stack1 : "")
    + "\" data-jcr=\""
    + container.escapeExpression((lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"defaultContent") : depth0),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":115,"column":156},"end":{"line":115,"column":179}}}))
    + "\"></div>\r\n";
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <div class=\"child-"
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"key") || (data && lookupProperty(data,"key"))) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"key","hash":{},"data":data,"loc":{"start":{"line":124,"column":22},"end":{"line":124,"column":30}}}) : helper)))
    + "\">\r\n      "
    + ((stack1 = container.lambda(depth0, depth0)) != null ? stack1 : "")
    + "\r\n    </div>\r\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\r\n<div class=\"container\">\r\n  <div class='loader'>\r\n    <svg focusable='false' xmlns='http://www.w3.org/2000/svg' width='1440' height='383'>\r\n      <defs>\r\n        <style type='text/css'>\r\n          .mhcls1{fill:#fff}.mhcls2{fill:url(#mh-lg)}.mhcls3{fill:url(#mh-lg-2)}.mhcls4{fill:url(#mh-lg-3)}.mhcls5{fill:url(#mh-lg-4)}.mhcls6{fill:url(#mh-lg-5)}.mhcls7{fill:url(#mh-lg-6)}\r\n        </style>\r\n        <linearGradient id='mh-lg' x1='1105.66' y1='63.16' x2='1141.58' y2='-54.33' gradientUnits='userSpaceOnUse'>\r\n          <stop offset='0' stop-color='#ededee'></stop>\r\n          <stop offset='.03' stop-color='#ededee' stop-opacity='.92'></stop>\r\n          <stop offset='.11' stop-color='#ededee' stop-opacity='.7'></stop>\r\n          <stop offset='.19' stop-color='#ededee' stop-opacity='.51'></stop>\r\n          <stop offset='.28' stop-color='#ededee' stop-opacity='.36'></stop>\r\n          <stop offset='.37' stop-color='#ededee' stop-opacity='.23'></stop>\r\n          <stop offset='.46' stop-color='#ededee' stop-opacity='.13'></stop>\r\n          <stop offset='.56' stop-color='#ededee' stop-opacity='.06'></stop>\r\n          <stop offset='.67' stop-color='#ededee' stop-opacity='.01'></stop>\r\n          <stop offset='.8' stop-color='#ededee' stop-opacity='0'></stop>\r\n        </linearGradient>\r\n        <linearGradient id='mh-lg-2' x1='668.16' y1='120.88' x2='722.9' y2='-38.1' gradientUnits='userSpaceOnUse'>\r\n          <stop offset='0' stop-color='#ededee'></stop>\r\n          <stop offset='.07' stop-color='#ededee' stop-opacity='.82'></stop>\r\n          <stop offset='.18' stop-color='#ededee' stop-opacity='.57'></stop>\r\n          <stop offset='.28' stop-color='#ededee' stop-opacity='.37'></stop>\r\n          <stop offset='.37' stop-color='#ededee' stop-opacity='.21'></stop>\r\n          <stop offset='.46' stop-color='#ededee' stop-opacity='.09'></stop>\r\n          <stop offset='.54' stop-color='#ededee' stop-opacity='.02'></stop>\r\n          <stop offset='.6' stop-color='#ededee' stop-opacity='0'></stop>\r\n        </linearGradient>\r\n        <linearGradient id='mh-lg-3' x1='1217.36' y1='358.03' x2='1439.93' y2='683.59' gradientUnits='userSpaceOnUse'>\r\n          <stop offset='0' stop-color='#ededee'></stop>\r\n          <stop offset='.02' stop-color='#ededee' stop-opacity='.83'></stop>\r\n          <stop offset='.04' stop-color='#ededee' stop-opacity='.66'></stop>\r\n          <stop offset='.07' stop-color='#ededee' stop-opacity='.5'></stop>\r\n          <stop offset='.1' stop-color='#ededee' stop-opacity='.36'></stop>\r\n          <stop offset='.13' stop-color='#ededee' stop-opacity='.25'></stop>\r\n          <stop offset='.17' stop-color='#ededee' stop-opacity='.16'></stop>\r\n          <stop offset='.21' stop-color='#ededee' stop-opacity='.09'></stop>\r\n          <stop offset='.27' stop-color='#ededee' stop-opacity='.04'></stop>\r\n          <stop offset='.34' stop-color='#ededee' stop-opacity='.01'></stop>\r\n          <stop offset='.55' stop-color='#ededee' stop-opacity='0'></stop>\r\n        </linearGradient>\r\n        <linearGradient id='mh-lg-4' x1='912.11' y1='206.71' x2='1081.23' y2='524.79' gradientUnits='userSpaceOnUse'>\r\n          <stop offset='0' stop-color='#ededee'></stop>\r\n          <stop offset='.03' stop-color='#ededee' stop-opacity='.87'></stop>\r\n          <stop offset='.07' stop-color='#ededee' stop-opacity='.69'></stop>\r\n          <stop offset='.12' stop-color='#ededee' stop-opacity='.52'></stop>\r\n          <stop offset='.17' stop-color='#ededee' stop-opacity='.38'></stop>\r\n          <stop offset='.22' stop-color='#ededee' stop-opacity='.26'></stop>\r\n          <stop offset='.28' stop-color='#ededee' stop-opacity='.17'></stop>\r\n          <stop offset='.35' stop-color='#ededee' stop-opacity='.09'></stop>\r\n          <stop offset='.44' stop-color='#ededee' stop-opacity='.04'></stop>\r\n          <stop offset='.55' stop-color='#ededee' stop-opacity='.01'></stop>\r\n          <stop offset='.82' stop-color='#ededee' stop-opacity='0'></stop>\r\n        </linearGradient>\r\n        <linearGradient id='mh-lg-5' x1='360.43' y1='226.88' x2='478.03' y2='415.07' gradientUnits='userSpaceOnUse'>\r\n          <stop offset='.43' stop-color='#ededee' stop-opacity='0'></stop>\r\n          <stop offset='.51' stop-color='#ededee' stop-opacity='.01'></stop>\r\n          <stop offset='.59' stop-color='#ededee' stop-opacity='.06'></stop>\r\n          <stop offset='.66' stop-color='#ededee' stop-opacity='.14'></stop>\r\n          <stop offset='.73' stop-color='#ededee' stop-opacity='.25'></stop>\r\n          <stop offset='.8' stop-color='#ededee' stop-opacity='.38'></stop>\r\n          <stop offset='.87' stop-color='#ededee' stop-opacity='.56'></stop>\r\n          <stop offset='.93' stop-color='#ededee' stop-opacity='.76'></stop>\r\n          <stop offset='1' stop-color='#ededee' stop-opacity='.99'></stop>\r\n          <stop offset='1' stop-color='#ededee'></stop>\r\n        </linearGradient>\r\n        <linearGradient id='mh-lg-6' x1='196.23' y1='90.31' x2='279.82' y2='254.36' gradientUnits='userSpaceOnUse'>\r\n          <stop offset='.22' stop-color='#ededee' stop-opacity='0'></stop>\r\n          <stop offset='.43' stop-color='#ededee' stop-opacity='.01'></stop>\r\n          <stop offset='.54' stop-color='#ededee' stop-opacity='.04'></stop>\r\n          <stop offset='.62' stop-color='#ededee' stop-opacity='.1'></stop>\r\n          <stop offset='.7' stop-color='#ededee' stop-opacity='.18'></stop>\r\n          <stop offset='.76' stop-color='#ededee' stop-opacity='.28'></stop>\r\n          <stop offset='.83' stop-color='#ededee' stop-opacity='.41'></stop>\r\n          <stop offset='.88' stop-color='#ededee' stop-opacity='.57'></stop>\r\n          <stop offset='.94' stop-color='#ededee' stop-opacity='.74'></stop>\r\n          <stop offset='.99' stop-color='#ededee' stop-opacity='.94'></stop>\r\n          <stop offset='1' stop-color='#ededee'></stop>\r\n        </linearGradient>\r\n      </defs>\r\n      <g>\r\n        <path class='mhcls1' d='M0 0h1440v500H0z'></path>\r\n        <path class='mhcls1' d='M0 0h1440v500H0z'></path>\r\n        <path class='mhcls2' d='M930.67 0c68.12 22.17 134.3 45.46 190.86 68 61.1-23.82 135.63-49.57 197.75-68z'></path>\r\n        <path\r\n          class='mhcls3'\r\n          d='M831.62 0H317.56C450.43 36 665 108.76 854.2 184.94c74.84-36.53 143.36-67.59 217.86-98C996.23 56 912.12 26.24 831.62 0z'\r\n        ></path>\r\n        <path class='mhcls4' d='M1440 500V252c-143.29 72.29-286.08 157.09-420.72 248z'></path>\r\n        <path\r\n          class='mhcls5'\r\n          d='M1420 0c-337.49 97.83-697.12 293.07-989.75 500h471.63c179.64-118.83 364.55-220.63 538.12-298.91V0z'\r\n        ></path>\r\n        <path\r\n          class='mhcls6'\r\n          d='M322.79 500C472.11 396 646 289.68 809.63 207.87c-71.75-28.71-152-59.9-208-80.1-154 61.34-379.86 179.3-572.09 307.45 46.79 21.44 92.14 42.6 136.86 64.77z'\r\n        ></path>\r\n        <path\r\n          class='mhcls7'\r\n          d='M0 394.14C176.81 284 390.72 175.71 558.52 112.36c-65.2-23.5-149.46-51.67-207.81-69.57A3528.27 3528.27 0 0 0 0 190.29z'\r\n        ></path>\r\n      </g>\r\n    </svg>\r\n  </div>\r\n  <div id='mastheadPartialContent' class='partial-content' tabindex='-1'>\r\n  </div>\r\n</div>\r\n<div id=\"mbox-"
    + ((stack1 = container.lambda(((stack1 = ((stack1 = (data && lookupProperty(data,"root"))) && lookupProperty(stack1,"module"))) && lookupProperty(stack1,"adContainer")), depth0)) != null ? stack1 : "")
    + "\"></div>\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"site") : depth0)) != null ? lookupProperty(stack1,"mockShieldResponse") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":112,"column":2},"end":{"line":116,"column":9}}})) != null ? stack1 : "")
    + "</script>\r\n\r\n<div id='jcr-defaults' data-jcr=\""
    + container.escapeExpression((lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"jcrDefaults") : depth0),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":119,"column":33},"end":{"line":119,"column":53}}}))
    + "\"></div>\r\n<div id='jcr-video-controls' data-jcr=\""
    + container.escapeExpression((lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"videoControls") : depth0),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":120,"column":39},"end":{"line":120,"column":61}}}))
    + "\"></div>\r\n\r\n<div class=\"child-logic-container\">\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"children") : depth0),{"name":"each","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":123,"column":2},"end":{"line":127,"column":11}}})) != null ? stack1 : "")
    + "</div>\r\n";
},"useData":true});
const renderTemplate$a = function (data, options) {
  return Template$a(data, options);
};

const Template$9 = Handlebars.template({"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"animation-controls\">\r\n  "
    + ((stack1 = ((helper = (helper = lookupProperty(helpers,"animationPauseButton") || (depth0 != null ? lookupProperty(depth0,"animationPauseButton") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"animationPauseButton","hash":{},"data":data,"loc":{"start":{"line":3,"column":2},"end":{"line":3,"column":28}}}) : helper))) != null ? stack1 : "")
    + "\r\n  "
    + ((stack1 = ((helper = (helper = lookupProperty(helpers,"animationPlayButton") || (depth0 != null ? lookupProperty(depth0,"animationPlayButton") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"animationPlayButton","hash":{},"data":data,"loc":{"start":{"line":4,"column":2},"end":{"line":4,"column":27}}}) : helper))) != null ? stack1 : "")
    + "\r\n</div>\r\n";
},"useData":true});
const renderTemplate$9 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$9(data, options);
};

const Template$8 = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"each","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":2,"column":2},"end":{"line":7,"column":11}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <div class=\"logo logo-"
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"index") || (data && lookupProperty(data,"index"))) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"index","hash":{},"data":data,"loc":{"start":{"line":3,"column":26},"end":{"line":3,"column":36}}}) : helper)))
    + "\">\r\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"inlineContentPartial"),depth0,{"name":"inlineContentPartial","data":data,"indent":"      ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "      "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":5,"column":6},"end":{"line":5,"column":22}}}))
    + "\r\n    </div>\r\n";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <div class=\"logo\">\r\n"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"inlineContentPartial"),depth0,{"name":"inlineContentPartial","data":data,"indent":"    ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "    "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":11,"column":4},"end":{"line":11,"column":20}}}))
    + "\r\n  </div>\r\n";
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"large") : depth0)) != null ? lookupProperty(stack1,"inlineContent") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":2},"end":{"line":18,"column":9}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"medium") : depth0)) != null ? lookupProperty(stack1,"inlineContent") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":19,"column":2},"end":{"line":21,"column":9}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"small") : depth0)) != null ? lookupProperty(stack1,"inlineContent") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(11, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":22,"column":2},"end":{"line":24,"column":9}}})) != null ? stack1 : "");
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <span class=\"show-for-large-up\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"large") : depth0)) != null ? lookupProperty(stack1,"inlineContent") : stack1), depth0)) != null ? stack1 : "")
    + "</span>\r\n";
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <span class=\"show-for-medium-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"medium") : depth0)) != null ? lookupProperty(stack1,"inlineContent") : stack1), depth0)) != null ? stack1 : "")
    + "</span>\r\n";
},"11":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <span class=\"show-for-small-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"small") : depth0)) != null ? lookupProperty(stack1,"inlineContent") : stack1), depth0)) != null ? stack1 : "")
    + "</span>\r\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"isArray")||(depth0 && lookupProperty(depth0,"isArray"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"isArray","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.program(4, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":1,"column":0},"end":{"line":13,"column":12}}})) != null ? stack1 : "")
    + "\r\n";
},"main_d":  function(fn, props, container, depth0, data, blockParams, depths) {

  var decorators = container.decorators, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(6, data, 0, blockParams, depths),"inverse":container.noop,"args":["inlineContentPartial"],"data":data,"loc":{"start":{"line":15,"column":0},"end":{"line":25,"column":11}}}) || fn;
  return fn;
  }

,"useDecorators":true,"usePartial":true,"useData":true,"useDepths":true});
const renderTemplate$8 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$8(data, options);
};

const Template$7 = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\r\n"
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"default") : depth0),{"name":"with","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":6,"column":2},"end":{"line":10,"column":11}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"small") : depth0),{"name":"with","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":14,"column":2},"end":{"line":20,"column":11}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"medium") : depth0),{"name":"with","hash":{},"fn":container.program(7, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":24,"column":2},"end":{"line":30,"column":11}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"large") : depth0),{"name":"with","hash":{},"fn":container.program(9, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":34,"column":2},"end":{"line":40,"column":11}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"each","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":4},"end":{"line":9,"column":13}}})) != null ? stack1 : "");
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"styleRulesPartial"),depth0,{"name":"styleRulesPartial","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  @media screen and (max-width: 767px) {\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"each","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":4},"end":{"line":18,"column":13}}})) != null ? stack1 : "")
    + "  }\r\n";
},"7":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  @media screen and (min-width: 768px) and (max-width: 1024px) {\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"each","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":26,"column":4},"end":{"line":28,"column":13}}})) != null ? stack1 : "")
    + "  }\r\n";
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  @media screen and (min-width: 1025px) {\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"each","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":36,"column":4},"end":{"line":38,"column":13}}})) != null ? stack1 : "")
    + "  }\r\n";
},"11":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"isArray")||(depth0 && lookupProperty(depth0,"isArray"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"selector") : depth0),{"name":"isArray","hash":{},"fn":container.program(12, data, 0),"inverse":container.program(18, data, 0),"data":data,"loc":{"start":{"line":45,"column":2},"end":{"line":58,"column":14}}})) != null ? stack1 : "");
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    "
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"selector") : depth0),{"name":"each","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":46,"column":4},"end":{"line":47,"column":24}}})) != null ? stack1 : "")
    + " {\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"properties") : depth0),{"name":"each","hash":{},"fn":container.program(16, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":48,"column":6},"end":{"line":50,"column":15}}})) != null ? stack1 : "")
    + "    }\r\n";
},"13":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ".masthead-container-module .partial-container "
    + ((stack1 = container.lambda(depth0, depth0)) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),(data && lookupProperty(data,"last")),{"name":"unless","hash":{},"fn":container.program(14, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":46,"column":78},"end":{"line":47,"column":15}}})) != null ? stack1 : "");
},"14":function(container,depth0,helpers,partials,data) {
    return ",\r\n    ";
},"16":function(container,depth0,helpers,partials,data) {
    var stack1, helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "        "
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"key") || (data && lookupProperty(data,"key"))) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"key","hash":{},"data":data,"loc":{"start":{"line":49,"column":8},"end":{"line":49,"column":16}}}) : helper)))
    + ": "
    + ((stack1 = container.lambda(depth0, depth0)) != null ? stack1 : "")
    + ";\r\n";
},"18":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    .masthead-container-module .partial-container "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"selector") : depth0),{"name":"if","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":53,"column":50},"end":{"line":53,"column":87}}})) != null ? stack1 : "")
    + " {\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"properties") : depth0),{"name":"each","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":54,"column":6},"end":{"line":56,"column":15}}})) != null ? stack1 : "")
    + "    }\r\n";
},"19":function(container,depth0,helpers,partials,data) {
    var stack1, helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = ((helper = (helper = lookupProperty(helpers,"selector") || (depth0 != null ? lookupProperty(depth0,"selector") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"selector","hash":{},"data":data,"loc":{"start":{"line":53,"column":66},"end":{"line":53,"column":80}}}) : helper))) != null ? stack1 : "");
},"21":function(container,depth0,helpers,partials,data) {
    var helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "        "
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"key") || (data && lookupProperty(data,"key"))) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"key","hash":{},"data":data,"loc":{"start":{"line":55,"column":8},"end":{"line":55,"column":16}}}) : helper)))
    + ": "
    + container.escapeExpression(container.lambda(depth0, depth0))
    + ";\r\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<style type=\"text/css\">\r\n"
    + ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"inlineStyles") : depth0),{"name":"with","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":42,"column":9}}})) != null ? stack1 : "")
    + "\r\n\r\n\r\n</style>\r\n";
},"main_d":  function(fn, props, container, depth0, data, blockParams, depths) {

  var decorators = container.decorators, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(11, data, 0, blockParams, depths),"inverse":container.noop,"args":["styleRulesPartial"],"data":data,"loc":{"start":{"line":44,"column":0},"end":{"line":59,"column":11}}}) || fn;
  return fn;
  }

,"useDecorators":true,"usePartial":true,"useData":true,"useDepths":true});
const renderTemplate$7 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$7(data, options);
};

const Template$6 = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"layerContent")||(depth0 && lookupProperty(depth0,"layerContent"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"layerContent","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":2},"end":{"line":8,"column":19}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"content") : depth0),{"name":"each","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":4},"end":{"line":7,"column":13}}})) != null ? stack1 : "");
},"3":function(container,depth0,helpers,partials,data) {
    var stack1;

  return "      "
    + ((stack1 = container.lambda(depth0, depth0)) != null ? stack1 : "")
    + "\r\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"layer-content-container\">\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"layerContent") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":9,"column":9}}})) != null ? stack1 : "")
    + "</div>\r\n";
},"useData":true});
const renderTemplate$6 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$6(data, options);
};

const Template$5 = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"layerContent")||(depth0 && lookupProperty(depth0,"layerContent"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),depth0,{"name":"layerContent","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":2},"end":{"line":13,"column":19}}})) != null ? stack1 : "");
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"content") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":4},"end":{"line":12,"column":11}}})) != null ? stack1 : "");
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <p>"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"text1") : stack1), depth0)) != null ? stack1 : "")
    + "</p>\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"productLinks") : stack1),{"name":"each","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":6},"end":{"line":11,"column":15}}})) != null ? stack1 : "");
},"4":function(container,depth0,helpers,partials,data) {
    var stack1;

  return "        <div class=\"columns small-12 products-link\">"
    + ((stack1 = container.lambda(depth0, depth0)) != null ? stack1 : "")
    + "\r\n          <span class=\"drawer-arrow\"></span>\r\n        </div>\r\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"layer-content-container\">\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"layerContent") : depth0),{"name":"each","hash":{},"fn":container.program(1, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":0},"end":{"line":14,"column":9}}})) != null ? stack1 : "")
    + "</div>\r\n";
},"useData":true});
const renderTemplate$5 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$5(data, options);
};

const Template$4 = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    return "with-inline-callout";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <div class=\"product\">\r\n        <div class=\"img-container\">\r\n          "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"image") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":8,"column":10},"end":{"line":8,"column":34}}}))
    + " "
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"primaryCta") : depth0), depth0)) != null ? stack1 : "")
    + "\r\n        </div>\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"productContent") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":8},"end":{"line":45,"column":15}}})) != null ? stack1 : "")
    + "      </div>\r\n";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"productContent") : depth0),{"name":"each","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":10},"end":{"line":44,"column":19}}})) != null ? stack1 : "");
},"5":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","link",{"name":"compare","hash":{},"fn":container.program(6, data, 0),"inverse":container.program(8, data, 0),"data":data,"loc":{"start":{"line":12,"column":12},"end":{"line":43,"column":24}}})) != null ? stack1 : "");
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"linkPartial"),depth0,{"name":"linkPartial","data":data,"indent":"              ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","p",{"name":"compare","hash":{},"fn":container.program(9, data, 0),"inverse":container.program(11, data, 0),"data":data,"loc":{"start":{"line":15,"column":12},"end":{"line":42,"column":24}}})) != null ? stack1 : "");
},"9":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"textPartial"),depth0,{"name":"textPartial","data":data,"indent":"              ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"11":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","headline",{"name":"compare","hash":{},"fn":container.program(12, data, 0),"inverse":container.program(14, data, 0),"data":data,"loc":{"start":{"line":18,"column":12},"end":{"line":41,"column":24}}})) != null ? stack1 : "");
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"headlinePartial"),depth0,{"name":"headlinePartial","data":data,"indent":"              ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"14":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","subHeadline",{"name":"compare","hash":{},"fn":container.program(15, data, 0),"inverse":container.program(17, data, 0),"data":data,"loc":{"start":{"line":21,"column":12},"end":{"line":40,"column":24}}})) != null ? stack1 : "");
},"15":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"subHeadlinePartial"),depth0,{"name":"subHeadlinePartial","data":data,"indent":"              ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"17":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","list",{"name":"compare","hash":{},"fn":container.program(18, data, 0),"inverse":container.program(26, data, 0),"data":data,"loc":{"start":{"line":24,"column":12},"end":{"line":39,"column":24}}})) != null ? stack1 : "");
},"18":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"style") : depth0),"==","ul",{"name":"compare","hash":{},"fn":container.program(19, data, 0),"inverse":container.program(21, data, 0),"data":data,"loc":{"start":{"line":25,"column":14},"end":{"line":32,"column":26}}})) != null ? stack1 : "");
},"19":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                <ul>"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"listPartial"),depth0,{"name":"listPartial","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</ul>\r\n";
},"21":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"style") : depth0),"==","ol",{"name":"compare","hash":{},"fn":container.program(22, data, 0),"inverse":container.program(24, data, 0),"data":data,"loc":{"start":{"line":28,"column":14},"end":{"line":31,"column":26}}})) != null ? stack1 : "");
},"22":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                <ol>"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"listPartial"),depth0,{"name":"listPartial","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</ol>\r\n";
},"24":function(container,depth0,helpers,partials,data) {
    return "";
},"26":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","rating",{"name":"compare","hash":{},"fn":container.program(27, data, 0),"inverse":container.program(24, data, 0),"data":data,"loc":{"start":{"line":34,"column":12},"end":{"line":38,"column":24}}})) != null ? stack1 : "");
},"27":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "              "
    + ((stack1 = (lookupProperty(helpers,"starRating")||(depth0 && lookupProperty(depth0,"starRating"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"rating") : depth0),{"name":"starRating","hash":{},"fn":container.program(24, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":35,"column":14},"end":{"line":35,"column":56}}})) != null ? stack1 : "")
    + "\r\n              <p>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"ratingText") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\r\n";
},"29":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"with").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"promo") : depth0)) != null ? lookupProperty(stack1,"inlineCallout") : stack1),{"name":"with","hash":{},"fn":container.program(30, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":49,"column":6},"end":{"line":53,"column":15}}})) != null ? stack1 : "");
},"30":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "        <div class=\"inline-callout\" "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"bgColor") : depth0),{"name":"if","hash":{},"fn":container.program(31, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":50,"column":36},"end":{"line":50,"column":88}}})) != null ? stack1 : "")
    + ">\r\n          "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":51,"column":10},"end":{"line":51,"column":38}}}))
    + "\r\n        </div>\r\n";
},"31":function(container,depth0,helpers,partials,data) {
    var helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "style=\"background:"
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"bgColor") || (depth0 != null ? lookupProperty(depth0,"bgColor") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"bgColor","hash":{},"data":data,"loc":{"start":{"line":50,"column":69},"end":{"line":50,"column":80}}}) : helper)))
    + "\"";
},"33":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"position") : depth0),"==","top",{"name":"compare","hash":{},"fn":container.program(34, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":62,"column":4},"end":{"line":64,"column":16}}})) != null ? stack1 : "");
},"34":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"promoPartial"),depth0,{"name":"promoPartial","data":data,"indent":"      ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"36":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <div class=\"columns small-content-container-flexible\">\r\n        <div class=\"row collapse\">\r\n          <div class=\"cardImage columns small-"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"mobileLayout") : depths[1]),"==","halves",{"name":"compare","hash":{},"fn":container.program(37, data, 0, blockParams, depths),"inverse":container.program(39, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":70,"column":46},"end":{"line":70,"column":183}}})) != null ? stack1 : "")
    + " small-image-container-flexible\">\r\n              "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"image") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":71,"column":14},"end":{"line":71,"column":38}}}))
    + "\r\n          </div>\r\n          <div class=\"columns small-"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"mobileLayout") : depths[1]),"==","halves",{"name":"compare","hash":{},"fn":container.program(37, data, 0, blockParams, depths),"inverse":container.program(42, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":73,"column":36},"end":{"line":73,"column":173}}})) != null ? stack1 : "")
    + " flexible-content-items-container\">\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"productContent") : depth0),{"name":"each","hash":{},"fn":container.program(45, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":74,"column":12},"end":{"line":107,"column":21}}})) != null ? stack1 : "")
    + "          </div>\r\n        </div>\r\n      </div>\r\n";
},"37":function(container,depth0,helpers,partials,data) {
    return "6";
},"39":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"mobileLayout") : depths[1]),"==","thirds",{"name":"compare","hash":{},"fn":container.program(40, data, 0, blockParams, depths),"inverse":container.program(37, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":70,"column":102},"end":{"line":70,"column":171}}})) != null ? stack1 : "");
},"40":function(container,depth0,helpers,partials,data) {
    return "4";
},"42":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"mobileLayout") : depths[1]),"==","thirds",{"name":"compare","hash":{},"fn":container.program(43, data, 0, blockParams, depths),"inverse":container.program(37, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":73,"column":92},"end":{"line":73,"column":161}}})) != null ? stack1 : "");
},"43":function(container,depth0,helpers,partials,data) {
    return "8";
},"45":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","link",{"name":"compare","hash":{},"fn":container.program(46, data, 0),"inverse":container.program(48, data, 0),"data":data,"loc":{"start":{"line":75,"column":14},"end":{"line":106,"column":26}}})) != null ? stack1 : "");
},"46":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"linkPartial"),depth0,{"name":"linkPartial","data":data,"indent":"                ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"48":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","p",{"name":"compare","hash":{},"fn":container.program(49, data, 0),"inverse":container.program(51, data, 0),"data":data,"loc":{"start":{"line":78,"column":14},"end":{"line":105,"column":26}}})) != null ? stack1 : "");
},"49":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"textPartial"),depth0,{"name":"textPartial","data":data,"indent":"                ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"51":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","headline",{"name":"compare","hash":{},"fn":container.program(52, data, 0),"inverse":container.program(54, data, 0),"data":data,"loc":{"start":{"line":81,"column":14},"end":{"line":104,"column":26}}})) != null ? stack1 : "");
},"52":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"headlinePartial"),depth0,{"name":"headlinePartial","data":data,"indent":"                ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"54":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","subHeadline",{"name":"compare","hash":{},"fn":container.program(55, data, 0),"inverse":container.program(57, data, 0),"data":data,"loc":{"start":{"line":84,"column":14},"end":{"line":103,"column":26}}})) != null ? stack1 : "");
},"55":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"subHeadlinePartial"),depth0,{"name":"subHeadlinePartial","data":data,"indent":"                ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"57":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","list",{"name":"compare","hash":{},"fn":container.program(18, data, 0),"inverse":container.program(58, data, 0),"data":data,"loc":{"start":{"line":87,"column":14},"end":{"line":102,"column":26}}})) != null ? stack1 : "");
},"58":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","rating",{"name":"compare","hash":{},"fn":container.program(59, data, 0),"inverse":container.program(24, data, 0),"data":data,"loc":{"start":{"line":97,"column":14},"end":{"line":101,"column":26}}})) != null ? stack1 : "");
},"59":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                "
    + ((stack1 = (lookupProperty(helpers,"starRating")||(depth0 && lookupProperty(depth0,"starRating"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"rating") : depth0),{"name":"starRating","hash":{},"fn":container.program(24, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":98,"column":16},"end":{"line":98,"column":58}}})) != null ? stack1 : "")
    + "\r\n                  <p>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"ratingText") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\r\n";
},"61":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"position") : depth0),"==","bottom",{"name":"compare","hash":{},"fn":container.program(62, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":114,"column":8},"end":{"line":116,"column":20}}})) != null ? stack1 : "");
},"62":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"promoPartial"),depth0,{"name":"promoPartial","data":data,"indent":"          ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"64":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <p>\r\n    "
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"unless","hash":{},"fn":container.program(65, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":125,"column":4},"end":{"line":125,"column":78}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(67, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":125,"column":79},"end":{"line":126,"column":74}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(69, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":126,"column":75},"end":{"line":127,"column":78}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"small") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(71, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":127,"column":79},"end":{"line":128,"column":76}}})) != null ? stack1 : "")
    + "\r\n  </p>\r\n";
},"65":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <span>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"content") : depth0), depth0)) != null ? stack1 : "")
    + "</span> ";
},"67":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\r\n    <span class=\"show-for-large-up\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"69":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\r\n    <span class=\"show-for-medium-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"71":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\r\n    <span class=\"show-for-small-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"small") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"73":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <div>\r\n    "
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"unless","hash":{},"fn":container.program(74, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":135,"column":4},"end":{"line":135,"column":63}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(76, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":136,"column":4},"end":{"line":138,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(78, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":139,"column":4},"end":{"line":141,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"small") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(80, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":142,"column":4},"end":{"line":144,"column":11}}})) != null ? stack1 : "")
    + "  </div>\r\n";
},"74":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"content") : depth0), depth0)) != null ? stack1 : "");
},"76":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <span class=\"show-for-large-up\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1), depth0)) != null ? stack1 : "")
    + "</span>\r\n";
},"78":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <span class=\"show-for-medium-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1), depth0)) != null ? stack1 : "")
    + "</span>\r\n";
},"80":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <span class=\"show-for-small-only mobile-cta\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"small") : stack1), depth0)) != null ? stack1 : "")
    + "</span>\r\n";
},"82":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"content") : depth0),{"name":"each","hash":{},"fn":container.program(83, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":150,"column":2},"end":{"line":160,"column":11}}})) != null ? stack1 : "");
},"83":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"icon") : depth0),{"name":"if","hash":{},"fn":container.program(84, data, 0),"inverse":container.program(91, data, 0),"data":data,"loc":{"start":{"line":151,"column":2},"end":{"line":159,"column":9}}})) != null ? stack1 : "");
},"84":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <li class=\"cnx-regular list-image\" style=\""
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"url") : stack1),{"name":"if","hash":{},"fn":container.program(85, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":152,"column":46},"end":{"line":152,"column":117}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"position") : stack1),{"name":"if","hash":{},"fn":container.program(87, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":152,"column":117},"end":{"line":152,"column":196}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"size") : stack1),{"name":"if","hash":{},"fn":container.program(89, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":152,"column":196},"end":{"line":152,"column":263}}})) != null ? stack1 : "")
    + "\">\r\n      <span>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"copy") : depth0), depth0)) != null ? stack1 : "")
    + "</span>\r\n    </li>\r\n";
},"85":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "background-image:url("
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"url") : stack1), depth0)) != null ? stack1 : "")
    + "); ";
},"87":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "background-position:"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"position") : stack1), depth0)) != null ? stack1 : "")
    + "; ";
},"89":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "background-size:"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"size") : stack1), depth0)) != null ? stack1 : "")
    + "; ";
},"91":function(container,depth0,helpers,partials,data) {
    var stack1;

  return "    <li class=\"cnx-regular\">\r\n      <span>"
    + ((stack1 = container.lambda(depth0, depth0)) != null ? stack1 : "")
    + "</span>\r\n    </li>\r\n";
},"93":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <h4 class=\"card-headline\">\r\n    "
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"unless","hash":{},"fn":container.program(94, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":166,"column":4},"end":{"line":166,"column":65}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(67, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":166,"column":66},"end":{"line":167,"column":74}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(69, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":167,"column":75},"end":{"line":168,"column":78}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"small") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(71, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":168,"column":79},"end":{"line":169,"column":76}}})) != null ? stack1 : "")
    + "\r\n  </h4>\r\n";
},"94":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"content") : depth0), depth0)) != null ? stack1 : "")
    + " ";
},"96":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <p class=\"card-sub-headline\">\r\n    "
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"unless","hash":{},"fn":container.program(94, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":176,"column":4},"end":{"line":176,"column":65}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(67, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":176,"column":66},"end":{"line":177,"column":74}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(69, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":177,"column":75},"end":{"line":178,"column":78}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"small") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(71, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":178,"column":79},"end":{"line":179,"column":76}}})) != null ? stack1 : "")
    + "\r\n  </p>\r\n";
},"98":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <img src=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"dividerImage") : depth0), depth0))
    + "\">\r\n";
},"100":function(container,depth0,helpers,partials,data) {
    var stack1, helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <div class='promo-copy cnx-regular'>\r\n    <p style=\""
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"copySize") : depth0),{"name":"if","hash":{},"fn":container.program(101, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":189,"column":14},"end":{"line":189,"column":61}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"copyColor") : depth0),{"name":"if","hash":{},"fn":container.program(103, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":189,"column":62},"end":{"line":189,"column":107}}})) != null ? stack1 : "")
    + "\">"
    + ((stack1 = ((helper = (helper = lookupProperty(helpers,"content") || (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"content","hash":{},"data":data,"loc":{"start":{"line":189,"column":109},"end":{"line":189,"column":122}}}) : helper))) != null ? stack1 : "")
    + "</p>\r\n  </div>\r\n";
},"101":function(container,depth0,helpers,partials,data) {
    var helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "font-size: "
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"copySize") || (depth0 != null ? lookupProperty(depth0,"copySize") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"copySize","hash":{},"data":data,"loc":{"start":{"line":189,"column":41},"end":{"line":189,"column":53}}}) : helper)))
    + ";";
},"103":function(container,depth0,helpers,partials,data) {
    var helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "color: "
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"copyColor") || (depth0 != null ? lookupProperty(depth0,"copyColor") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"copyColor","hash":{},"data":data,"loc":{"start":{"line":189,"column":86},"end":{"line":189,"column":99}}}) : helper)))
    + ";";
},"105":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <div class=\"row show-for-small-only promo-mobile-content "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"stretched") : depth0),{"name":"if","hash":{},"fn":container.program(106, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":194,"column":61},"end":{"line":194,"column":94}}})) != null ? stack1 : "")
    + "\" "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"bgColor") : depth0),{"name":"if","hash":{},"fn":container.program(31, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":194,"column":96},"end":{"line":194,"column":148}}})) != null ? stack1 : "")
    + ">\r\n      "
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","image",{"name":"compare","hash":{},"fn":container.program(108, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":195,"column":6},"end":{"line":195,"column":78}}})) != null ? stack1 : "")
    + "\r\n      "
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","copy",{"name":"compare","hash":{},"fn":container.program(110, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":196,"column":6},"end":{"line":196,"column":64}}})) != null ? stack1 : "")
    + "\r\n    </div>\r\n";
},"106":function(container,depth0,helpers,partials,data) {
    return "stretched";
},"108":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":195,"column":37},"end":{"line":195,"column":65}}}))
    + " ";
},"110":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"promoCopy"),depth0,{"name":"promoCopy","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + " ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"row show-for-medium-up collapse\">\r\n  <div class=\"columns large-8 large-push-2 content-section\">\r\n    <div class=\"products-container "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"promo") : depth0)) != null ? lookupProperty(stack1,"inlineCallout") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":35},"end":{"line":4,"column":93}}})) != null ? stack1 : "")
    + "\">\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"products") : depth0),{"name":"each","hash":{},"fn":container.program(3, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":5,"column":6},"end":{"line":47,"column":15}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"promo") : depth0)) != null ? lookupProperty(stack1,"inlineCallout") : stack1),{"name":"if","hash":{},"fn":container.program(29, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":48,"column":6},"end":{"line":54,"column":13}}})) != null ? stack1 : "")
    + "    </div>\r\n    "
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"infoAboutReviews") : depth0), depth0)) != null ? stack1 : "")
    + "\r\n  </div>\r\n</div>\r\n\r\n<div class=\"row collapse show-for-small-only extra-margin-bottom\">\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"promo") : depth0)) != null ? lookupProperty(stack1,"mobileContent") : stack1),{"name":"each","hash":{},"fn":container.program(33, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":61,"column":2},"end":{"line":65,"column":11}}})) != null ? stack1 : "")
    + "  <div class='block-container'>\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"products") : depth0),{"name":"each","hash":{},"fn":container.program(36, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":67,"column":4},"end":{"line":111,"column":13}}})) != null ? stack1 : "")
    + "  </div>\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"promo") : depth0)) != null ? lookupProperty(stack1,"mobileContent") : stack1),{"name":"each","hash":{},"fn":container.program(61, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":113,"column":6},"end":{"line":117,"column":15}}})) != null ? stack1 : "")
    + "</div>\r\n\r\n\r\n\r\n\r\n\r\n\r\n\r\n\r\n\r\n\r\n\r\n";
},"main_d":  function(fn, props, container, depth0, data, blockParams, depths) {

  var decorators = container.decorators, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(64, data, 0, blockParams, depths),"inverse":container.noop,"args":["textPartial"],"data":data,"loc":{"start":{"line":123,"column":0},"end":{"line":130,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(73, data, 0, blockParams, depths),"inverse":container.noop,"args":["linkPartial"],"data":data,"loc":{"start":{"line":133,"column":0},"end":{"line":146,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(82, data, 0, blockParams, depths),"inverse":container.noop,"args":["listPartial"],"data":data,"loc":{"start":{"line":149,"column":0},"end":{"line":161,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(93, data, 0, blockParams, depths),"inverse":container.noop,"args":["headlinePartial"],"data":data,"loc":{"start":{"line":164,"column":0},"end":{"line":171,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(96, data, 0, blockParams, depths),"inverse":container.noop,"args":["subHeadlinePartial"],"data":data,"loc":{"start":{"line":174,"column":0},"end":{"line":181,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(98, data, 0, blockParams, depths),"inverse":container.noop,"args":["dividerPartial"],"data":data,"loc":{"start":{"line":183,"column":0},"end":{"line":185,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(100, data, 0, blockParams, depths),"inverse":container.noop,"args":["promoCopy"],"data":data,"loc":{"start":{"line":187,"column":0},"end":{"line":191,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(105, data, 0, blockParams, depths),"inverse":container.noop,"args":["promoPartial"],"data":data,"loc":{"start":{"line":193,"column":0},"end":{"line":198,"column":11}}}) || fn;
  return fn;
  }

,"useDecorators":true,"usePartial":true,"useData":true,"useDepths":true});
const renderTemplate$4 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$4(data, options);
};

const Template$3 = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0)) != null ? lookupProperty(stack1,"style") : stack1), depth0));
},"3":function(container,depth0,helpers,partials,data) {
    return " full-width";
},"5":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <div class=\"masthead-background video-placeholder\" aria-hidden=\"true\">"
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":4,"column":74},"end":{"line":4,"column":107}}}))
    + "</div>\r\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\"video-background"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0)) != null ? lookupProperty(stack1,"style") : stack1),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(3, data, 0),"data":data,"loc":{"start":{"line":1,"column":28},"end":{"line":1,"column":119}}})) != null ? stack1 : "")
    + "\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"if","hash":{},"fn":container.program(5, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":3,"column":2},"end":{"line":5,"column":9}}})) != null ? stack1 : "")
    + "  <video id=\"backgroundVideoMasthead\" class=\"video-background__video hide\" "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0)) != null ? lookupProperty(stack1,"attributes") : stack1), depth0))
    + ">\r\n    <source class=\"mp4-source\" type=\"video/mp4\">\r\n    <source class=\"webm-source\" type=\"video/webm\">\r\n  </video>\r\n</div>\r\n";
},"useData":true});
const renderTemplate$3 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$3(data, options);
};

const Template$2 = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "        <div class=\"masthead-background\" aria-hidden=\"true\">"
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":5,"column":60},"end":{"line":5,"column":93}}}))
    + "</div>\r\n";
},"3":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "            <h2 class=\"cnx-regular headline "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"overrides") : depth0)) != null ? lookupProperty(stack1,"floatHeadline") : stack1),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":12,"column":44},"end":{"line":12,"column":119}}})) != null ? stack1 : "")
    + "\">\r\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"headline") : depth0)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"unless","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":13,"column":14},"end":{"line":15,"column":25}}})) != null ? stack1 : "")
    + "              "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"headline") : depth0)) != null ? lookupProperty(stack1,"large") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(8, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":14},"end":{"line":16,"column":119}}})) != null ? stack1 : "")
    + "\r\n              "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"headline") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(10, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":17,"column":14},"end":{"line":17,"column":124}}})) != null ? stack1 : "")
    + "\r\n              "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"headline") : depth0)) != null ? lookupProperty(stack1,"small") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(12, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":18,"column":14},"end":{"line":18,"column":121}}})) != null ? stack1 : "")
    + "\r\n            </h2>\r\n";
},"4":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"overrides") : depth0)) != null ? lookupProperty(stack1,"floatHeadline") : stack1), depth0));
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                "
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"headline") : depth0), depth0)) != null ? stack1 : "")
    + "\r\n";
},"8":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"show-for-large-up\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"headline") : depth0)) != null ? lookupProperty(stack1,"large") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"10":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"show-for-medium-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"headline") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"show-for-small-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"headline") : depth0)) != null ? lookupProperty(stack1,"small") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"14":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "            <p class=\"cnx-regular copy "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"overrides") : depth0)) != null ? lookupProperty(stack1,"floatCopy") : stack1),{"name":"if","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":22,"column":39},"end":{"line":22,"column":106}}})) != null ? stack1 : "")
    + "\">\r\n"
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"unless","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":23,"column":14},"end":{"line":34,"column":25}}})) != null ? stack1 : "")
    + "            "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"large") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(23, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":35,"column":12},"end":{"line":35,"column":109}}})) != null ? stack1 : "")
    + "\r\n            "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(25, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":36,"column":12},"end":{"line":36,"column":114}}})) != null ? stack1 : "")
    + "\r\n            "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"small") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(27, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":37,"column":12},"end":{"line":37,"column":111}}})) != null ? stack1 : "")
    + "\r\n            </p>\r\n";
},"15":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"overrides") : depth0)) != null ? lookupProperty(stack1,"floatCopy") : stack1), depth0));
},"17":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"bullets") : stack1),{"name":"unless","hash":{},"fn":container.program(18, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":24,"column":16},"end":{"line":26,"column":27}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"bullets") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(20, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":27,"column":16},"end":{"line":33,"column":23}}})) != null ? stack1 : "");
},"18":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                  "
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"copy") : depth0), depth0)) != null ? stack1 : "")
    + "\r\n";
},"20":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                  <ul>\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"bullets") : stack1),{"name":"each","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":29,"column":20},"end":{"line":31,"column":29}}})) != null ? stack1 : "")
    + "                  </ul>\r\n";
},"21":function(container,depth0,helpers,partials,data) {
    var stack1;

  return "                      <li><span>"
    + ((stack1 = container.lambda(depth0, depth0)) != null ? stack1 : "")
    + "</span></li>\r\n";
},"23":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"show-for-large-up\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"large") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"25":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"show-for-medium-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"27":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"show-for-small-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"small") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"29":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"cta-container primaryCta "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"overrides") : depth0)) != null ? lookupProperty(stack1,"floatCta") : stack1),{"name":"if","hash":{},"fn":container.program(30, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":40,"column":71},"end":{"line":40,"column":136}}})) != null ? stack1 : "")
    + "\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"primaryCta") : depth0), depth0)) != null ? stack1 : "")
    + "</span>";
},"30":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"overrides") : depth0)) != null ? lookupProperty(stack1,"floatCta") : stack1), depth0));
},"32":function(container,depth0,helpers,partials,data) {
    return "with-inline-callout";
},"34":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "              <div class=\"product\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"productHeading") : depth0),{"name":"if","hash":{},"fn":container.program(35, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":50,"column":16},"end":{"line":52,"column":23}}})) != null ? stack1 : "")
    + "                <div class=\"img-container\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"barkerData") : depth0),{"name":"if","hash":{},"fn":container.program(37, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":54,"column":18},"end":{"line":56,"column":25}}})) != null ? stack1 : "")
    + "                  "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"image") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":57,"column":18},"end":{"line":57,"column":42}}}))
    + "\r\n                </div>\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"productContent") : depth0),{"name":"if","hash":{},"fn":container.program(39, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":59,"column":16},"end":{"line":91,"column":23}}})) != null ? stack1 : "")
    + "              </div>\r\n";
},"35":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"textPartial"),(depth0 != null ? lookupProperty(depth0,"productHeading") : depth0),{"name":"textPartial","data":data,"indent":"                  ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"37":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                    "
    + ((stack1 = (lookupProperty(helpers,"barker")||(depth0 && lookupProperty(depth0,"barker"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"barkerData") : depth0)) != null ? lookupProperty(stack1,"text") : stack1),((stack1 = (depth0 != null ? lookupProperty(depth0,"barkerData") : depth0)) != null ? lookupProperty(stack1,"position") : stack1),null,((stack1 = (depth0 != null ? lookupProperty(depth0,"barkerData") : depth0)) != null ? lookupProperty(stack1,"color") : stack1),{"name":"barker","hash":{},"data":data,"loc":{"start":{"line":55,"column":20},"end":{"line":55,"column":105}}})) != null ? stack1 : "")
    + "\r\n";
},"39":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"productContent") : depth0),{"name":"each","hash":{},"fn":container.program(40, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":60,"column":18},"end":{"line":90,"column":27}}})) != null ? stack1 : "");
},"40":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","link",{"name":"compare","hash":{},"fn":container.program(41, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":61,"column":20},"end":{"line":63,"column":32}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","p",{"name":"compare","hash":{},"fn":container.program(43, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":65,"column":20},"end":{"line":67,"column":32}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","headline",{"name":"compare","hash":{},"fn":container.program(45, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":69,"column":20},"end":{"line":71,"column":32}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","subHeadline",{"name":"compare","hash":{},"fn":container.program(47, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":73,"column":20},"end":{"line":75,"column":32}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","list",{"name":"compare","hash":{},"fn":container.program(49, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":77,"column":20},"end":{"line":84,"column":32}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","rating",{"name":"compare","hash":{},"fn":container.program(54, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":86,"column":20},"end":{"line":89,"column":32}}})) != null ? stack1 : "");
},"41":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"linkPartial"),depth0,{"name":"linkPartial","data":data,"indent":"                      ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"43":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"textPartial"),depth0,{"name":"textPartial","data":data,"indent":"                      ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"45":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"headlinePartial"),depth0,{"name":"headlinePartial","data":data,"indent":"                      ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"47":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"subHeadlinePartial"),depth0,{"name":"subHeadlinePartial","data":data,"indent":"                      ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"49":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"style") : depth0),"==","ul",{"name":"compare","hash":{},"fn":container.program(50, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":78,"column":22},"end":{"line":80,"column":34}}})) != null ? stack1 : "")
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"style") : depth0),"==","ol",{"name":"compare","hash":{},"fn":container.program(52, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":81,"column":22},"end":{"line":83,"column":34}}})) != null ? stack1 : "");
},"50":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                        <ul>"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"listPartial"),depth0,{"name":"listPartial","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</ul>\r\n";
},"52":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                        <ol>"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"listPartial"),depth0,{"name":"listPartial","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</ol>\r\n";
},"54":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                      "
    + ((stack1 = (lookupProperty(helpers,"starRating")||(depth0 && lookupProperty(depth0,"starRating"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"rating") : depth0),{"name":"starRating","hash":{},"fn":container.program(55, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":87,"column":22},"end":{"line":87,"column":64}}})) != null ? stack1 : "")
    + "\r\n                        <p>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"ratingText") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\r\n";
},"55":function(container,depth0,helpers,partials,data) {
    return "";
},"57":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"position") : depth0),"==","top",{"name":"compare","hash":{},"fn":container.program(58, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":100,"column":12},"end":{"line":102,"column":24}}})) != null ? stack1 : "");
},"58":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"promoPartial"),depth0,{"name":"promoPartial","data":data,"indent":"              ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"60":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "              <div class=\"columns small-content-container-flexible\">\r\n                <div class=\"row collapse\">\r\n                  <div class=\"cardImage columns small-"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"mobileLayout") : depths[1]),"==","halves",{"name":"compare","hash":{},"fn":container.program(61, data, 0, blockParams, depths),"inverse":container.program(63, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":108,"column":54},"end":{"line":108,"column":191}}})) != null ? stack1 : "")
    + " small-image-container-flexible\">\r\n                      "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"image") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":109,"column":22},"end":{"line":109,"column":46}}}))
    + "\r\n                  </div>\r\n                  <div class=\"columns small-"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"mobileLayout") : depths[1]),"==","halves",{"name":"compare","hash":{},"fn":container.program(61, data, 0, blockParams, depths),"inverse":container.program(66, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":111,"column":44},"end":{"line":111,"column":181}}})) != null ? stack1 : "")
    + " flexible-content-items-container\">\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"productContent") : depth0),{"name":"each","hash":{},"fn":container.program(69, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":112,"column":20},"end":{"line":142,"column":29}}})) != null ? stack1 : "")
    + "                  </div>\r\n                </div>\r\n              </div>\r\n";
},"61":function(container,depth0,helpers,partials,data) {
    return "6";
},"63":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"mobileLayout") : depths[1]),"==","thirds",{"name":"compare","hash":{},"fn":container.program(64, data, 0, blockParams, depths),"inverse":container.program(61, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":108,"column":110},"end":{"line":108,"column":179}}})) != null ? stack1 : "");
},"64":function(container,depth0,helpers,partials,data) {
    return "4";
},"66":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depths[1] != null ? lookupProperty(depths[1],"mobileLayout") : depths[1]),"==","thirds",{"name":"compare","hash":{},"fn":container.program(67, data, 0, blockParams, depths),"inverse":container.program(61, data, 0, blockParams, depths),"data":data,"loc":{"start":{"line":111,"column":100},"end":{"line":111,"column":169}}})) != null ? stack1 : "");
},"67":function(container,depth0,helpers,partials,data) {
    return "8";
},"69":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","link",{"name":"compare","hash":{},"fn":container.program(70, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":113,"column":22},"end":{"line":115,"column":34}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","p",{"name":"compare","hash":{},"fn":container.program(72, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":117,"column":22},"end":{"line":119,"column":34}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","headline",{"name":"compare","hash":{},"fn":container.program(74, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":121,"column":22},"end":{"line":123,"column":34}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","subHeadline",{"name":"compare","hash":{},"fn":container.program(76, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":125,"column":22},"end":{"line":127,"column":34}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","list",{"name":"compare","hash":{},"fn":container.program(78, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":129,"column":22},"end":{"line":136,"column":34}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","rating",{"name":"compare","hash":{},"fn":container.program(83, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":138,"column":22},"end":{"line":141,"column":34}}})) != null ? stack1 : "");
},"70":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"linkPartial"),depth0,{"name":"linkPartial","data":data,"indent":"                        ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"72":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"textPartial"),depth0,{"name":"textPartial","data":data,"indent":"                        ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"74":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"headlinePartial"),depth0,{"name":"headlinePartial","data":data,"indent":"                        ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"76":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"subHeadlinePartial"),depth0,{"name":"subHeadlinePartial","data":data,"indent":"                        ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"78":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"style") : depth0),"==","ul",{"name":"compare","hash":{},"fn":container.program(79, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":130,"column":24},"end":{"line":132,"column":36}}})) != null ? stack1 : "")
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"style") : depth0),"==","ol",{"name":"compare","hash":{},"fn":container.program(81, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":133,"column":24},"end":{"line":135,"column":36}}})) != null ? stack1 : "");
},"79":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                          <ul>"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"listPartial"),depth0,{"name":"listPartial","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</ul>\r\n";
},"81":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                          <ol>"
    + ((stack1 = container.invokePartial(lookupProperty(partials,"listPartial"),depth0,{"name":"listPartial","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + "</ol>\r\n";
},"83":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                        "
    + ((stack1 = (lookupProperty(helpers,"starRating")||(depth0 && lookupProperty(depth0,"starRating"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"rating") : depth0),{"name":"starRating","hash":{},"fn":container.program(55, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":139,"column":24},"end":{"line":139,"column":66}}})) != null ? stack1 : "")
    + "\r\n                          <p>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"ratingText") : depth0), depth0)) != null ? stack1 : "")
    + "</p>\r\n";
},"85":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"position") : depth0),"==","bottom",{"name":"compare","hash":{},"fn":container.program(86, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":149,"column":16},"end":{"line":151,"column":28}}})) != null ? stack1 : "");
},"86":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.invokePartial(lookupProperty(partials,"promoPartial"),depth0,{"name":"promoPartial","data":data,"indent":"                  ","helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "");
},"88":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <p>\r\n    "
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"unless","hash":{},"fn":container.program(89, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":169,"column":4},"end":{"line":169,"column":78}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(91, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":169,"column":79},"end":{"line":170,"column":74}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(93, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":170,"column":75},"end":{"line":171,"column":78}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"small") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(95, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":171,"column":79},"end":{"line":172,"column":76}}})) != null ? stack1 : "")
    + "\r\n  </p>\r\n";
},"89":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " <span>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"content") : depth0), depth0)) != null ? stack1 : "")
    + "</span> ";
},"91":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\r\n    <span class=\"show-for-large-up\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"93":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\r\n    <span class=\"show-for-medium-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"95":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "\r\n    <span class=\"show-for-small-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"small") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"97":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <div class=\"link-content\">\r\n    "
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"unless","hash":{},"fn":container.program(98, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":179,"column":4},"end":{"line":179,"column":63}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(100, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":180,"column":4},"end":{"line":182,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(102, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":183,"column":4},"end":{"line":185,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"small") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(104, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":186,"column":4},"end":{"line":188,"column":11}}})) != null ? stack1 : "")
    + "  </div>\r\n";
},"98":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"content") : depth0), depth0)) != null ? stack1 : "");
},"100":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <span class=\"show-for-large-up\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1), depth0)) != null ? stack1 : "")
    + "</span>\r\n";
},"102":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <span class=\"show-for-medium-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1), depth0)) != null ? stack1 : "")
    + "</span>\r\n";
},"104":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      <span class=\"show-for-small-only mobile-cta\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"small") : stack1), depth0)) != null ? stack1 : "")
    + "</span>\r\n";
},"106":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"content") : depth0),{"name":"each","hash":{},"fn":container.program(107, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":194,"column":2},"end":{"line":204,"column":11}}})) != null ? stack1 : "");
},"107":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"icon") : depth0),{"name":"if","hash":{},"fn":container.program(108, data, 0),"inverse":container.program(115, data, 0),"data":data,"loc":{"start":{"line":195,"column":2},"end":{"line":203,"column":9}}})) != null ? stack1 : "");
},"108":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <li class=\"cnx-regular list-image\" style=\""
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"url") : stack1),{"name":"if","hash":{},"fn":container.program(109, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":196,"column":46},"end":{"line":196,"column":117}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"position") : stack1),{"name":"if","hash":{},"fn":container.program(111, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":196,"column":117},"end":{"line":196,"column":196}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"size") : stack1),{"name":"if","hash":{},"fn":container.program(113, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":196,"column":196},"end":{"line":196,"column":263}}})) != null ? stack1 : "")
    + "\">\r\n      <span>"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"copy") : depth0), depth0)) != null ? stack1 : "")
    + "</span>\r\n    </li>\r\n";
},"109":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "background-image:url("
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"url") : stack1), depth0)) != null ? stack1 : "")
    + "); ";
},"111":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "background-position:"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"position") : stack1), depth0)) != null ? stack1 : "")
    + "; ";
},"113":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "background-size:"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"icon") : depth0)) != null ? lookupProperty(stack1,"size") : stack1), depth0)) != null ? stack1 : "")
    + "; ";
},"115":function(container,depth0,helpers,partials,data) {
    var stack1;

  return "    <li class=\"cnx-regular\">\r\n      <span>"
    + ((stack1 = container.lambda(depth0, depth0)) != null ? stack1 : "")
    + "</span>\r\n    </li>\r\n";
},"117":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <h4 class=\"card-headline\">\r\n    "
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"unless","hash":{},"fn":container.program(118, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":210,"column":4},"end":{"line":210,"column":65}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(91, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":210,"column":66},"end":{"line":211,"column":74}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(93, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":211,"column":75},"end":{"line":212,"column":78}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"small") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(95, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":212,"column":79},"end":{"line":213,"column":76}}})) != null ? stack1 : "")
    + "\r\n  </h4>\r\n";
},"118":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"content") : depth0), depth0)) != null ? stack1 : "")
    + " ";
},"120":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <p class=\"card-sub-headline\">\r\n    "
    + ((stack1 = lookupProperty(helpers,"unless").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1),{"name":"unless","hash":{},"fn":container.program(118, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":220,"column":4},"end":{"line":220,"column":65}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"large") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(91, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":220,"column":66},"end":{"line":221,"column":74}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"medium") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(93, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":221,"column":75},"end":{"line":222,"column":78}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? lookupProperty(stack1,"small") : stack1)) != null ? lookupProperty(stack1,"length") : stack1),{"name":"if","hash":{},"fn":container.program(95, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":222,"column":79},"end":{"line":223,"column":76}}})) != null ? stack1 : "")
    + "\r\n  </p>\r\n";
},"122":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <img src=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"dividerImage") : depth0), depth0))
    + "\">\r\n";
},"124":function(container,depth0,helpers,partials,data) {
    var stack1, helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <div class='promo-copy cnx-regular'>\r\n    <p style=\""
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"copySize") : depth0),{"name":"if","hash":{},"fn":container.program(125, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":233,"column":14},"end":{"line":233,"column":61}}})) != null ? stack1 : "")
    + " "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"copyColor") : depth0),{"name":"if","hash":{},"fn":container.program(127, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":233,"column":62},"end":{"line":233,"column":107}}})) != null ? stack1 : "")
    + "\">"
    + ((stack1 = ((helper = (helper = lookupProperty(helpers,"content") || (depth0 != null ? lookupProperty(depth0,"content") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"content","hash":{},"data":data,"loc":{"start":{"line":233,"column":109},"end":{"line":233,"column":122}}}) : helper))) != null ? stack1 : "")
    + "</p>\r\n  </div>\r\n";
},"125":function(container,depth0,helpers,partials,data) {
    var helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "font-size: "
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"copySize") || (depth0 != null ? lookupProperty(depth0,"copySize") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"copySize","hash":{},"data":data,"loc":{"start":{"line":233,"column":41},"end":{"line":233,"column":53}}}) : helper)))
    + ";";
},"127":function(container,depth0,helpers,partials,data) {
    var helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "color: "
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"copyColor") || (depth0 != null ? lookupProperty(depth0,"copyColor") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"copyColor","hash":{},"data":data,"loc":{"start":{"line":233,"column":86},"end":{"line":233,"column":99}}}) : helper)))
    + ";";
},"129":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <div class=\"row show-for-small-only promo-mobile-content "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"stretched") : depth0),{"name":"if","hash":{},"fn":container.program(130, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":238,"column":61},"end":{"line":238,"column":94}}})) != null ? stack1 : "")
    + "\" "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"bgColor") : depth0),{"name":"if","hash":{},"fn":container.program(132, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":238,"column":96},"end":{"line":238,"column":148}}})) != null ? stack1 : "")
    + ">\r\n      "
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","image",{"name":"compare","hash":{},"fn":container.program(134, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":239,"column":6},"end":{"line":239,"column":78}}})) != null ? stack1 : "")
    + "\r\n      "
    + ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"type") : depth0),"==","copy",{"name":"compare","hash":{},"fn":container.program(136, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":240,"column":6},"end":{"line":240,"column":64}}})) != null ? stack1 : "")
    + "\r\n    </div>\r\n";
},"130":function(container,depth0,helpers,partials,data) {
    return "stretched";
},"132":function(container,depth0,helpers,partials,data) {
    var helper, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "style=\"background:"
    + container.escapeExpression(((helper = (helper = lookupProperty(helpers,"bgColor") || (depth0 != null ? lookupProperty(depth0,"bgColor") : depth0)) != null ? helper : container.hooks.helperMissing),(typeof helper === "function" ? helper.call(depth0 != null ? depth0 : (container.nullContext || {}),{"name":"bgColor","hash":{},"data":data,"loc":{"start":{"line":238,"column":129},"end":{"line":238,"column":140}}}) : helper)))
    + "\"";
},"134":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":239,"column":37},"end":{"line":239,"column":65}}}))
    + " ";
},"136":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return " "
    + ((stack1 = container.invokePartial(lookupProperty(partials,"promoCopy"),depth0,{"name":"promoCopy","data":data,"helpers":helpers,"partials":partials,"decorators":container.decorators})) != null ? stack1 : "")
    + " ";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data,blockParams,depths) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"theme") : depth0), depth0))
    + "\">\r\n  <div class=\"masthead-content\">\r\n    <div class=\"banner-content "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"params") : depth0)) != null ? lookupProperty(stack1,"bannerAlignment") : stack1), depth0))
    + "\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":6},"end":{"line":6,"column":13}}})) != null ? stack1 : "")
    + "\r\n      <div class=\"row headline-content multiple-cards\">\r\n        <div class=\"columns small-12 content-wrapper\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"headline") : depth0),{"name":"if","hash":{},"fn":container.program(3, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":11,"column":10},"end":{"line":20,"column":17}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"copy") : depth0),{"name":"if","hash":{},"fn":container.program(14, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":21,"column":10},"end":{"line":39,"column":17}}})) != null ? stack1 : "")
    + "          "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"primaryCta") : depth0),{"name":"if","hash":{},"fn":container.program(29, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":40,"column":10},"end":{"line":40,"column":173}}})) != null ? stack1 : "")
    + "\r\n        </div>\r\n      </div>\r\n\r\n      <div class=\"row multi-card-content\">\r\n        <div class=\"row show-for-medium-up collapse\">\r\n          <div class=\"columns medium-12 content-section\">\r\n            <div class=\"products-container "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"promo") : depth0)) != null ? lookupProperty(stack1,"inlineCallout") : stack1),{"name":"if","hash":{},"fn":container.program(32, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":47,"column":43},"end":{"line":47,"column":101}}})) != null ? stack1 : "")
    + "\">\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"products") : depth0),{"name":"each","hash":{},"fn":container.program(34, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":48,"column":14},"end":{"line":93,"column":23}}})) != null ? stack1 : "")
    + "            </div>\r\n          </div>\r\n        </div>\r\n\r\n        <div class=\"row collapse show-for-small-only extra-margin-bottom\">\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"promo") : depth0)) != null ? lookupProperty(stack1,"mobileContent") : stack1),{"name":"each","hash":{},"fn":container.program(57, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":99,"column":10},"end":{"line":103,"column":19}}})) != null ? stack1 : "")
    + "          <div class='block-container'>\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"products") : depth0),{"name":"each","hash":{},"fn":container.program(60, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":105,"column":12},"end":{"line":146,"column":21}}})) != null ? stack1 : "")
    + "          </div>\r\n"
    + ((stack1 = lookupProperty(helpers,"each").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"promo") : depth0)) != null ? lookupProperty(stack1,"mobileContent") : stack1),{"name":"each","hash":{},"fn":container.program(85, data, 0, blockParams, depths),"inverse":container.noop,"data":data,"loc":{"start":{"line":148,"column":14},"end":{"line":152,"column":23}}})) != null ? stack1 : "")
    + "        </div>\r\n      </div>\r\n\r\n    </div>\r\n  </div>\r\n</div>\r\n\r\n<div class=\"inline-styles\">\r\n  "
    + container.escapeExpression((lookupProperty(helpers,"include")||(depth0 && lookupProperty(depth0,"include"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),"_inline-styles",".inline-styles",{"name":"include","hash":{},"data":data,"loc":{"start":{"line":161,"column":2},"end":{"line":161,"column":47}}}))
    + "\r\n</div>\r\n\r\n\r\n\r\n\r\n\r\n\r\n\r\n\r\n\r\n\r\n";
},"main_d":  function(fn, props, container, depth0, data, blockParams, depths) {

  var decorators = container.decorators, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(88, data, 0, blockParams, depths),"inverse":container.noop,"args":["textPartial"],"data":data,"loc":{"start":{"line":167,"column":0},"end":{"line":174,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(97, data, 0, blockParams, depths),"inverse":container.noop,"args":["linkPartial"],"data":data,"loc":{"start":{"line":177,"column":0},"end":{"line":190,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(106, data, 0, blockParams, depths),"inverse":container.noop,"args":["listPartial"],"data":data,"loc":{"start":{"line":193,"column":0},"end":{"line":205,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(117, data, 0, blockParams, depths),"inverse":container.noop,"args":["headlinePartial"],"data":data,"loc":{"start":{"line":208,"column":0},"end":{"line":215,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(120, data, 0, blockParams, depths),"inverse":container.noop,"args":["subHeadlinePartial"],"data":data,"loc":{"start":{"line":218,"column":0},"end":{"line":225,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(122, data, 0, blockParams, depths),"inverse":container.noop,"args":["dividerPartial"],"data":data,"loc":{"start":{"line":227,"column":0},"end":{"line":229,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(124, data, 0, blockParams, depths),"inverse":container.noop,"args":["promoCopy"],"data":data,"loc":{"start":{"line":231,"column":0},"end":{"line":235,"column":11}}}) || fn;
  fn = lookupProperty(decorators,"inline")(fn,props,container,{"name":"inline","hash":{},"fn":container.program(129, data, 0, blockParams, depths),"inverse":container.noop,"args":["promoPartial"],"data":data,"loc":{"start":{"line":237,"column":0},"end":{"line":242,"column":11}}}) || fn;
  return fn;
  }

,"useDecorators":true,"usePartial":true,"useData":true,"useDepths":true});
const renderTemplate$2 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$2(data, options);
};

const Template$1 = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <a class=\"masthead-content "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"primaryCtaOptions") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":31},"end":{"line":10,"column":100}}})) != null ? stack1 : "")
    + "\"\r\n    id=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"primaryCtaId") : depth0), depth0))
    + "\"\r\n    href=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"primaryUrl") : depth0), depth0))
    + "\"\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"primaryCtaTarget") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":13,"column":4},"end":{"line":15,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"primaryCtaOptions") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":16,"column":4},"end":{"line":19,"column":11}}})) != null ? stack1 : "")
    + ">\r\n";
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"primaryCtaOptions") : depth0)) != null ? lookupProperty(stack1,"class") : stack1), depth0));
},"4":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      target=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"primaryCtaTarget") : depth0), depth0))
    + "\"\r\n";
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      rel=\""
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"primaryCtaOptions") : depth0)) != null ? lookupProperty(stack1,"rel") : stack1), depth0))
    + "\"\r\n      data-options=\""
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"primaryCtaOptions") : depth0)) != null ? lookupProperty(stack1,"dataOptions") : stack1), depth0))
    + "\"\r\n    ";
},"8":function(container,depth0,helpers,partials,data) {
    return "\r\n  <div class=\"masthead-content\">\r\n";
},"10":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "        <div class=\"video-background__container\">\r\n          "
    + container.escapeExpression((lookupProperty(helpers,"include")||(depth0 && lookupProperty(depth0,"include"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),"_video-background",".video-background__container",{"name":"include","hash":{},"data":data,"loc":{"start":{"line":28,"column":10},"end":{"line":28,"column":72}}}))
    + "\r\n        </div>\r\n";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"if","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":32,"column":8},"end":{"line":34,"column":15}}})) != null ? stack1 : "");
},"13":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "          <div class=\"masthead-background\" aria-hidden=\"true\">"
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":33,"column":62},"end":{"line":33,"column":95}}}))
    + "</div>\r\n";
},"15":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"secureLink")||(depth0 && lookupProperty(depth0,"secureLink"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"snrLink") : stack1),((stack1 = (depth0 != null ? lookupProperty(depth0,"site") : depth0)) != null ? lookupProperty(stack1,"secureHost") : stack1),{"name":"secureLink","hash":{},"data":data,"loc":{"start":{"line":36,"column":32},"end":{"line":36,"column":83}}})) != null ? stack1 : "");
},"17":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                "
    + ((stack1 = (lookupProperty(helpers,"barker")||(depth0 && lookupProperty(depth0,"barker"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"barkerData") : depth0)) != null ? lookupProperty(stack1,"text") : stack1),((stack1 = (depth0 != null ? lookupProperty(depth0,"barkerData") : depth0)) != null ? lookupProperty(stack1,"position") : stack1),null,((stack1 = (depth0 != null ? lookupProperty(depth0,"barkerData") : depth0)) != null ? lookupProperty(stack1,"color") : stack1),{"name":"barker","hash":{},"data":data,"loc":{"start":{"line":43,"column":16},"end":{"line":43,"column":101}}})) != null ? stack1 : "")
    + "\r\n";
},"19":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"cardImage") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":46,"column":16},"end":{"line":46,"column":42}}}))
    + "\r\n";
},"21":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"compare")||(depth0 && lookupProperty(depth0,"compare"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = ((stack1 = (depth0 != null ? lookupProperty(depth0,"buttons") : depth0)) != null ? lookupProperty(stack1,"attributes") : stack1)) != null ? lookupProperty(stack1,"id") : stack1),"===","overviewChooseAccountSettingTriggerSmall",{"name":"compare","hash":{},"fn":container.program(22, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":56,"column":16},"end":{"line":58,"column":28}}})) != null ? stack1 : "")
    + "                "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"BAsnrLink") : stack1),{"name":"if","hash":{},"fn":container.program(24, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":59,"column":16},"end":{"line":59,"column":104}}})) != null ? stack1 : "")
    + "\r\n                <div class=\"buttons\">\r\n                  "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"buttons") : depth0)) != null ? lookupProperty(stack1,"openNowBtn") : stack1),{"name":"if","hash":{},"fn":container.program(26, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":61,"column":18},"end":{"line":61,"column":103}}})) != null ? stack1 : "")
    + "\r\n                  "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"buttons") : depth0)) != null ? lookupProperty(stack1,"saveForLaterBtn") : stack1),{"name":"if","hash":{},"fn":container.program(28, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":62,"column":18},"end":{"line":62,"column":100}}})) != null ? stack1 : "")
    + "\r\n                  "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"buttons") : depth0)) != null ? lookupProperty(stack1,"viewCartSmall") : stack1),{"name":"if","hash":{},"fn":container.program(30, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":63,"column":18},"end":{"line":63,"column":130}}})) != null ? stack1 : "")
    + "\r\n                  "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"buttons") : depth0)) != null ? lookupProperty(stack1,"viewCartMedium") : stack1),{"name":"if","hash":{},"fn":container.program(32, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":64,"column":18},"end":{"line":64,"column":132}}})) != null ? stack1 : "")
    + "\r\n                </div>\r\n";
},"22":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                  <div>"
    + ((stack1 = (lookupProperty(helpers,"button")||(depth0 && lookupProperty(depth0,"button"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"buttons") : depth0),{"name":"button","hash":{},"data":data,"loc":{"start":{"line":57,"column":23},"end":{"line":57,"column":48}}})) != null ? stack1 : "")
    + "</div>\r\n";
},"24":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = (lookupProperty(helpers,"secureLink")||(depth0 && lookupProperty(depth0,"secureLink"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"BAsnrLink") : stack1),((stack1 = (depth0 != null ? lookupProperty(depth0,"site") : depth0)) != null ? lookupProperty(stack1,"secureHost") : stack1),{"name":"secureLink","hash":{},"data":data,"loc":{"start":{"line":59,"column":44},"end":{"line":59,"column":97}}})) != null ? stack1 : "");
},"26":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span>"
    + container.escapeExpression((lookupProperty(helpers,"button")||(depth0 && lookupProperty(depth0,"button"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"buttons") : depth0)) != null ? lookupProperty(stack1,"openNowBtn") : stack1),{"name":"button","hash":{},"data":data,"loc":{"start":{"line":61,"column":55},"end":{"line":61,"column":89}}}))
    + "</span>";
},"28":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression((lookupProperty(helpers,"button")||(depth0 && lookupProperty(depth0,"button"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"buttons") : depth0)) != null ? lookupProperty(stack1,"saveForLaterBtn") : stack1),{"name":"button","hash":{},"data":data,"loc":{"start":{"line":62,"column":54},"end":{"line":62,"column":93}}}));
},"30":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"hide-for-large-up\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"buttons") : depth0)) != null ? lookupProperty(stack1,"viewCartSmall") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"32":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<span class=\"show-for-large-up\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"buttons") : depth0)) != null ? lookupProperty(stack1,"viewCartMedium") : stack1), depth0)) != null ? stack1 : "")
    + "</span>";
},"34":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "            "
    + container.escapeExpression((lookupProperty(helpers,"include")||(depth0 && lookupProperty(depth0,"include"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),"_animation-controls",(depth0 != null ? lookupProperty(depth0,"videoControls") : depth0),{"name":"include","hash":{},"data":data,"loc":{"start":{"line":72,"column":12},"end":{"line":72,"column":64}}}))
    + "\r\n";
},"36":function(container,depth0,helpers,partials,data) {
    return "    </a>\r\n";
},"38":function(container,depth0,helpers,partials,data) {
    return "  </div>\r\n";
},"40":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <script type='application/json' id='jcr-video-data'>"
    + ((stack1 = (lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":90,"column":54},"end":{"line":90,"column":85}}})) != null ? stack1 : "")
    + "</script>\r\n";
},"42":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  "
    + container.escapeExpression((lookupProperty(helpers,"include")||(depth0 && lookupProperty(depth0,"include"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),"_sb-deposits-layer-content",{"name":"include","hash":{},"data":data,"loc":{"start":{"line":94,"column":2},"end":{"line":94,"column":42}}}))
    + "\r\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<style type=\"text/css\">\r\n  #snrLink {\r\n    background-image: url('"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"snrLinkBackground") : depth0), depth0)) != null ? stack1 : "")
    + "');\r\n  }\r\n</style>\r\n\r\n<div class=\"secure-sb-deposits\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"primaryButtonText") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(8, data, 0),"data":data,"loc":{"start":{"line":9,"column":2},"end":{"line":23,"column":9}}})) != null ? stack1 : "")
    + "    <div class=\"banner-content "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"params") : depth0)) != null ? lookupProperty(stack1,"bannerAlignment") : stack1), depth0))
    + "\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0),{"name":"if","hash":{},"fn":container.program(10, data, 0),"inverse":container.program(12, data, 0),"data":data,"loc":{"start":{"line":26,"column":6},"end":{"line":35,"column":13}}})) != null ? stack1 : "")
    + "      "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"LINKS") : depth0)) != null ? lookupProperty(stack1,"snrLink") : stack1),{"name":"if","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":36,"column":6},"end":{"line":36,"column":90}}})) != null ? stack1 : "")
    + "\r\n      <div class=\"row small-collapse masthead-copy\">\r\n        <div class=\"columns small-12 medium-12 large-12\">\r\n          <h2 class=\"headline show-for-small-only\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"headline") : depth0), depth0)) != null ? stack1 : "")
    + "</h2>\r\n          <div class=\"flex-items\">\r\n            <div class=\"card-image\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"barkerData") : depth0),{"name":"if","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":42,"column":14},"end":{"line":44,"column":21}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"cardImage") : depth0),{"name":"if","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":45,"column":14},"end":{"line":47,"column":21}}})) != null ? stack1 : "")
    + "            </div>\r\n            <div class=\"content-wrapper\">\r\n              <h2 class=\"headline show-for-medium-up\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"headline") : depth0), depth0)) != null ? stack1 : "")
    + "</h2>\r\n              <div class=\"copy\">\r\n                <p class=\"copy--large-bold\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"promo1") : stack1), depth0)) != null ? stack1 : "")
    + "</p>\r\n                <p class=\"copy--small show-for-small-up\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"promo2") : stack1), depth0)) != null ? stack1 : "")
    + "</p>\r\n              </div>\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"buttons") : depth0),{"name":"if","hash":{},"fn":container.program(21, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":55,"column":14},"end":{"line":66,"column":21}}})) != null ? stack1 : "")
    + "              </div>\r\n            </div>\r\n          </div>\r\n\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0),{"name":"if","hash":{},"fn":container.program(34, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":71,"column":10},"end":{"line":73,"column":17}}})) != null ? stack1 : "")
    + "        </div>\r\n      </div>\r\n    </div>\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"primaryButtonText") : depth0),{"name":"if","hash":{},"fn":container.program(36, data, 0),"inverse":container.program(38, data, 0),"data":data,"loc":{"start":{"line":78,"column":2},"end":{"line":82,"column":9}}})) != null ? stack1 : "")
    + "</div>\r\n<div class=\"inline-styles\">\r\n  "
    + container.escapeExpression((lookupProperty(helpers,"include")||(depth0 && lookupProperty(depth0,"include"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),"_inline-styles",".inline-styles",{"name":"include","hash":{},"data":data,"loc":{"start":{"line":86,"column":2},"end":{"line":86,"column":47}}}))
    + "\r\n</div>\r\n\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0),{"name":"if","hash":{},"fn":container.program(40, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":89,"column":0},"end":{"line":91,"column":7}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"layerContent") : depth0),{"name":"if","hash":{},"fn":container.program(42, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":93,"column":0},"end":{"line":95,"column":7}}})) != null ? stack1 : "");
},"useData":true});
const renderTemplate$1 = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template$1(data, options);
};

const Template = Handlebars.template({"1":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "    <a class=\"masthead-content "
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"primaryCtaOptions") : depth0),{"name":"if","hash":{},"fn":container.program(2, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":4,"column":31},"end":{"line":4,"column":100}}})) != null ? stack1 : "")
    + "\"\r\n    id=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"primaryCtaId") : depth0), depth0))
    + "\"\r\n    href=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"primaryUrl") : depth0), depth0))
    + "\"\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"primaryCtaTarget") : depth0),{"name":"if","hash":{},"fn":container.program(4, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":7,"column":4},"end":{"line":9,"column":11}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"primaryCtaOptions") : depth0),{"name":"if","hash":{},"fn":container.program(6, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":10,"column":4},"end":{"line":13,"column":11}}})) != null ? stack1 : "")
    + ">\r\n";
},"2":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"primaryCtaOptions") : depth0)) != null ? lookupProperty(stack1,"class") : stack1), depth0));
},"4":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      target=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"primaryCtaTarget") : depth0), depth0))
    + "\"\r\n";
},"6":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "      rel=\""
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"primaryCtaOptions") : depth0)) != null ? lookupProperty(stack1,"rel") : stack1), depth0))
    + "\"\r\n      data-options=\""
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"primaryCtaOptions") : depth0)) != null ? lookupProperty(stack1,"dataOptions") : stack1), depth0))
    + "\"\r\n    ";
},"8":function(container,depth0,helpers,partials,data) {
    return "\r\n  <div class=\"masthead-content\">\r\n";
},"10":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "        <div class=\"video-background__container\">\r\n          "
    + container.escapeExpression((lookupProperty(helpers,"include")||(depth0 && lookupProperty(depth0,"include"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),"_video-background",".video-background__container",{"name":"include","hash":{},"data":data,"loc":{"start":{"line":22,"column":10},"end":{"line":22,"column":72}}}))
    + "\r\n        </div>\r\n";
},"12":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"if","hash":{},"fn":container.program(13, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":26,"column":8},"end":{"line":28,"column":15}}})) != null ? stack1 : "");
},"13":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "          <div class=\"masthead-background\" aria-hidden=\"true\">"
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"imageInterchange") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":27,"column":62},"end":{"line":27,"column":95}}}))
    + "</div>\r\n";
},"15":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                "
    + ((stack1 = (lookupProperty(helpers,"barker")||(depth0 && lookupProperty(depth0,"barker"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),((stack1 = (depth0 != null ? lookupProperty(depth0,"barkerData") : depth0)) != null ? lookupProperty(stack1,"text") : stack1),((stack1 = (depth0 != null ? lookupProperty(depth0,"barkerData") : depth0)) != null ? lookupProperty(stack1,"position") : stack1),null,((stack1 = (depth0 != null ? lookupProperty(depth0,"barkerData") : depth0)) != null ? lookupProperty(stack1,"color") : stack1),{"name":"barker","hash":{},"data":data,"loc":{"start":{"line":36,"column":16},"end":{"line":36,"column":101}}})) != null ? stack1 : "")
    + "\r\n";
},"17":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "                "
    + container.escapeExpression((lookupProperty(helpers,"picture")||(depth0 && lookupProperty(depth0,"picture"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"cardImage") : depth0),{"name":"picture","hash":{},"data":data,"loc":{"start":{"line":39,"column":16},"end":{"line":39,"column":42}}}))
    + "\r\n";
},"19":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "            "
    + container.escapeExpression((lookupProperty(helpers,"include")||(depth0 && lookupProperty(depth0,"include"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),"_animation-controls",(depth0 != null ? lookupProperty(depth0,"videoControls") : depth0),{"name":"include","hash":{},"data":data,"loc":{"start":{"line":63,"column":12},"end":{"line":63,"column":64}}}))
    + "\r\n";
},"21":function(container,depth0,helpers,partials,data) {
    return "    </a>\r\n";
},"23":function(container,depth0,helpers,partials,data) {
    return "  </div>\r\n";
},"25":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  <script type='application/json' id='jcr-video-data'>"
    + ((stack1 = (lookupProperty(helpers,"json")||(depth0 && lookupProperty(depth0,"json"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0),{"name":"json","hash":{},"data":data,"loc":{"start":{"line":81,"column":54},"end":{"line":81,"column":85}}})) != null ? stack1 : "")
    + "</script>\r\n";
},"27":function(container,depth0,helpers,partials,data) {
    var lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "  "
    + container.escapeExpression((lookupProperty(helpers,"include")||(depth0 && lookupProperty(depth0,"include"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),"_layer-content",{"name":"include","hash":{},"data":data,"loc":{"start":{"line":85,"column":2},"end":{"line":85,"column":30}}}))
    + "\r\n";
},"compiler":[8,">= 4.3.0"],"main":function(container,depth0,helpers,partials,data) {
    var stack1, lookupProperty = container.lookupProperty || function(parent, propertyName) {
        if (Object.prototype.hasOwnProperty.call(parent, propertyName)) {
          return parent[propertyName];
        }
        return undefined
    };

  return "<div class=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"theme") : depth0), depth0))
    + "\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"primaryButtonText") : depth0),{"name":"if","hash":{},"fn":container.program(1, data, 0),"inverse":container.program(8, data, 0),"data":data,"loc":{"start":{"line":3,"column":2},"end":{"line":17,"column":9}}})) != null ? stack1 : "")
    + "    <div class=\"banner-content "
    + container.escapeExpression(container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"params") : depth0)) != null ? lookupProperty(stack1,"bannerAlignment") : stack1), depth0))
    + "\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0),{"name":"if","hash":{},"fn":container.program(10, data, 0),"inverse":container.program(12, data, 0),"data":data,"loc":{"start":{"line":20,"column":6},"end":{"line":29,"column":13}}})) != null ? stack1 : "")
    + "      <div class=\"row small-collapse masthead-copy\">\r\n        <div class=\"columns small-12 medium-12 large-12\">\r\n          <h2 class=\"headline show-for-small-only\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"headline") : depth0), depth0)) != null ? stack1 : "")
    + "</h2>\r\n          <div class=\"flex-items\">\r\n            <div class=\"card-image\">\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"barkerData") : depth0),{"name":"if","hash":{},"fn":container.program(15, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":35,"column":14},"end":{"line":37,"column":21}}})) != null ? stack1 : "")
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"cardImage") : depth0),{"name":"if","hash":{},"fn":container.program(17, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":38,"column":14},"end":{"line":40,"column":21}}})) != null ? stack1 : "")
    + "            </div>\r\n\r\n            <div class=\"content-wrapper\">\r\n              <h2 class=\"headline show-for-medium-up\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"headline") : depth0), depth0)) != null ? stack1 : "")
    + "</h2>\r\n\r\n              <div class=\"copy\">\r\n                <p class=\"copy--large-bold\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"promo1") : stack1), depth0)) != null ? stack1 : "")
    + "</p>\r\n                <p class=\"copy--small show-for-medium-up\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"promo2") : stack1), depth0)) != null ? stack1 : "")
    + "</p>\r\n              </div>\r\n\r\n              <div class=\"cta-container primaryCta show-for-medium-up\">\r\n                <span class=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"primaryButtonClasses") : depth0), depth0))
    + "\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"primaryButtonText") : depth0), depth0)) != null ? stack1 : "")
    + "</span>\r\n              </div>\r\n            </div>\r\n          </div>\r\n\r\n          <div class=\"cta-container primaryCta show-for-small-only\">\r\n            <span class=\""
    + container.escapeExpression(container.lambda((depth0 != null ? lookupProperty(depth0,"primaryButtonClasses") : depth0), depth0))
    + " spa-btn--block\">"
    + ((stack1 = container.lambda((depth0 != null ? lookupProperty(depth0,"primaryButtonText") : depth0), depth0)) != null ? stack1 : "")
    + "</span>\r\n          </div>\r\n          <p class=\"copy copy--small show-for-small-only\">"
    + ((stack1 = container.lambda(((stack1 = (depth0 != null ? lookupProperty(depth0,"copy") : depth0)) != null ? lookupProperty(stack1,"promo2") : stack1), depth0)) != null ? stack1 : "")
    + "</p>\r\n\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0),{"name":"if","hash":{},"fn":container.program(19, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":62,"column":10},"end":{"line":64,"column":17}}})) != null ? stack1 : "")
    + "        </div>\r\n      </div>\r\n    </div>\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"primaryButtonText") : depth0),{"name":"if","hash":{},"fn":container.program(21, data, 0),"inverse":container.program(23, data, 0),"data":data,"loc":{"start":{"line":69,"column":2},"end":{"line":73,"column":9}}})) != null ? stack1 : "")
    + "</div>\r\n<div class=\"inline-styles\">\r\n  "
    + container.escapeExpression((lookupProperty(helpers,"include")||(depth0 && lookupProperty(depth0,"include"))||container.hooks.helperMissing).call(depth0 != null ? depth0 : (container.nullContext || {}),"_inline-styles",".inline-styles",{"name":"include","hash":{},"data":data,"loc":{"start":{"line":77,"column":2},"end":{"line":77,"column":47}}}))
    + "\r\n</div>\r\n\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"videoBackground") : depth0),{"name":"if","hash":{},"fn":container.program(25, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":80,"column":0},"end":{"line":82,"column":7}}})) != null ? stack1 : "")
    + "\r\n"
    + ((stack1 = lookupProperty(helpers,"if").call(depth0 != null ? depth0 : (container.nullContext || {}),(depth0 != null ? lookupProperty(depth0,"layercontent") : depth0),{"name":"if","hash":{},"fn":container.program(27, data, 0),"inverse":container.noop,"data":data,"loc":{"start":{"line":84,"column":0},"end":{"line":86,"column":7}}})) != null ? stack1 : "");
},"useData":true});
const renderTemplate = function (data, options) {
  options = options || {};options.helpers = options.helpers || {};options.partials = options.partials || {};
  options.helpers = {...options.helpers, ..._moduleConfig.helpers };
  options.partials = {...options.partials, ..._moduleConfig.partials };
  return Template(data, options);
};

// Import templates & helpers
var _moduleConfig = {
  "name": "@sparta.sales.modules/masthead-container-module",
  "version": "7.0.2",
  "id": "mastheadContainer",
  "containerID": "salesMastheadContainerModule",
  "moduleName": "sales-masthead-container-module",
  "semantic": true,
  "superModule": false,
  "viewPort": ["small", "medium", "large"],
  "tag": "div",
  "nameSpace": "masthead-container-module",
  "data": {
    "init": "MastheadContainer",
    "options": {}
  },
  "moduleParams": [{
    "name": "adContainer",
    "type": "String",
    "values": [],
    "default": "BOA_SECURE_SB_DEPOSITS_MASTHEAD",
    "description": "String name for default container for masthead."
  }, {
    "name": "containerType",
    "type": "String",
    "values": [],
    "default": "masthead",
    "description": "String name for container type"
  }, {
    "name": "defaultTemplate",
    "type": "String",
    "values": [],
    "default": "secure-sb-deposits",
    "description": "String name for the default template"
  }, {
    "name": "defaultContentKey",
    "type": "String",
    "values": [],
    "default": null,
    "description": "Default shield content key without language prefix"
  }, {
    "name": "bannerAlignment",
    "type": "String",
    "values": ["left", "center", "right"],
    "default": "center",
    "description": "Alignment justification for masthead banner content"
  }],
  "layout": "layout",
  "render": function (context) {
    return renderTemplate$a(context, {
      "helpers": {
        layerContent,
        button,
        compare,
        json,
        picture,
        assetUrl,
        cinemaHelper
      },
      "partials": {
        "_animation-controls": renderTemplate$9,
        "_card-image": renderTemplate$8,
        "_inline-styles": renderTemplate$7,
        "_layer-content": renderTemplate$6,
        "_sb-deposits-layer-content": renderTemplate$5,
        "_three-card-flexible-content-body": renderTemplate$4,
        "_video-background": renderTemplate$3,
        "multi-card": renderTemplate$2,
        "secure-sb-deposits": renderTemplate$1,
        "standard": renderTemplate
      }
    });
  },
  "template": renderTemplate$a,
  "partials": {
    "_animation-controls": renderTemplate$9,
    "_card-image": renderTemplate$8,
    "_inline-styles": renderTemplate$7,
    "_layer-content": renderTemplate$6,
    "_sb-deposits-layer-content": renderTemplate$5,
    "_three-card-flexible-content-body": renderTemplate$4,
    "_video-background": renderTemplate$3,
    "multi-card": renderTemplate$2,
    "secure-sb-deposits": renderTemplate$1,
    "standard": renderTemplate
  },
  "helpers": {
    layerContent,
    button,
    compare,
    json,
    picture,
    assetUrl,
    cinemaHelper
  },
  "dependencies": {
    "@sparta.sales.utilities/secure-link-utility": "^1.0.0",
    "@sparta.global.helpers/button-helper": "^1.0.0",
    "@sparta.global.helpers/comparisons-helper": "^1.0.0",
    "@sparta.global.helpers/json-helper": "^1.0.0",
    "@sparta.global.helpers/sparta-helper": "^1.0.0",
    "@sparta.global.helpers/picture-helper": "^1.0.0",
    "@sparta.global.helpers/cinema-helper": "^3.0.0",
    "@sparta.credit-cards.helpers/credit-cards": "^3.0.0",
    "@sparta.global.utilities/sparta-events-utility": "^1.0.0",
    "@sparta.global.utilities/sparta-global-functions-utility": "^1.0.0",
    "@sparta.global.utilities/sparta-module-utility": "^1.0.0",
    "@sparta.global.utilities/sparta-style-token-utility": "^1.0.0",
    "@sparta.global.vendors/handlebars": "^4.7.7",
    "@sparta.global.vendors/jquery": "^3.2.1",
    "@sparta.global.vendors/lodash": "^4.17.21",
    "@sparta.sales.utilities/animation-utility": "^1.0.0",
    "@sparta.sales.utilities/modal-utility": "^3.0.0",
    "@sparta.sales.utilities/tagging-utility": "^1.0.0",
    "@sparta.sales.utilities/targeting-utility": "^2.0.0",
    "@sparta.global.utilities/sparta-mediaquery-utility": "^1.0.0"
  },
  "stores": []
};

/* eslint-disable no-console */
/* global spaParams console */
/* eslint-disable no-console class-methods-use-this */

const SELECTORS = {
  DATA_AD_CONTAINER: 'data-ad-container',
  PARTIAL_CONTENT: '.partial-content',
  MASTHEAD_CHILDREN: '.masthead-children',
  MASTHEAD_DEFAULT: '#masthead-default',
  JCR_DEFAULTS: '#jcr-defaults',
  MASTHEAD_CONTENT: '.masthead-content',
  JCR_VIDEO_CONTROLS: '#jcr-video-controls'
};
const QS = {
  PREVIEW_CONTAINER_QS: 'containerId',
  PREVIEW_CONTENT_QS: 'contentKey'
};
const EVENTS = {
  SHIELD_CONTENT_RENDERED: 'shieldContentRendered'
};

/**
 * MastheadContainerModuleJS Class
 * @extends Module
 */
class MastheadContainer extends Module {
  /**
   * Creates an instance of MastheadContainerModuleJS
   * @param {object} options options
   * @param {object} context context
   */
  constructor(options, context) {
    super({
      ...options,
      context,
      config: _moduleConfig
    });
    const {
      target
    } = this.settings;
    const jcrVideoControls = JSON.parse(target.querySelector(SELECTORS.JCR_VIDEO_CONTROLS).dataset.jcr);
    this.adContainer = this.params.adContainer;
    this.defaultTemplate = this.params.defaultTemplate;
    this.containerType = this.params.containerType;
    this.bannerAlignment = this.params.bannerAlignment;
    this.adContainerEl = target.querySelector(`[${SELECTORS.DATA_AD_CONTAINER}=${this.adContainer}]`);
    this.partialContainer = target.querySelector(SELECTORS.PARTIAL_CONTENT);
    this.mastheadChildrenContainer = target.querySelector(SELECTORS.MASTHEAD_CHILDREN);
    this.defaultContent = JSON.parse(target.querySelector(SELECTORS.MASTHEAD_DEFAULT).dataset.jcr);
    this.jcrDefaults = JSON.parse(target.querySelector(SELECTORS.JCR_DEFAULTS).dataset.jcr);
    this.jcrVideoControls = jcrVideoControls || {};
    this.usingDefaultContent = false;
    this.contentLoaded = new Set();
    this.mockShieldResponse = spaParams.siteParams.mockShieldResponse;
  }

  /**
   * Initializes the module
   * @returns {void}
   */
  init() {
    MastheadContainer.registerHelpers();
    if (this.hasPreviewQS()) {
      this.adContainerEl = this.settings.target.querySelector(`[${SELECTORS.DATA_AD_CONTAINER}=${this.previewContainerId.toLowerCase()}]`);
    }
    this.bindEventSubscriptions();
    if (this.adContainerEl) {
      targetingUtility(this.adContainerEl, this.mockShieldResponse);
    }
  }

  /**
   * Bind event subscriptions so that we can load the offer when AJAX request completes
   * @returns {void}
   */
  bindEventSubscriptions() {
    // Shield ad container content subscription
    spartaEventsUtility.sub(`target-${this.adContainer}`, this.loadContent.bind(this), true);
  }

  /**
   * Checks preview query string params
   * @returns {Boolean} has preview qs
   */
  hasPreviewQS() {
    this.previewContainerId = spartaGlobalFunctions.getUrlParam(QS.PREVIEW_CONTAINER_QS);
    this.previewContentKey = spartaGlobalFunctions.getUrlParam(QS.PREVIEW_CONTENT_QS);
    return this.previewContainerId && this.previewContentKey;
  }

  /**
   * Load content
   *
   * @param {Object} event - The event object from sparta events utility
   * @param {Object} event.data - The data sent with the event
   * @param {Object} event.data.contentResponse - The content data from Shield
   * @param {Object} event.data.adobeJsonObj - The adobe data from mbox
   * @returns {void}
   */
  loadContent({
    data: {
      contentResponse,
      adobeJsonObj
    }
  }) {
    const eventData = contentResponse && contentResponse.META ? contentResponse : this.defaultContent;
    const data = {
      META: {
        TEMPLATE: this.defaultTemplate
      },
      ...eventData
    };
    this.templateName = data.META.TEMPLATE;
    this.renderContent(eventData, adobeJsonObj);
    this.updateSecureLinks();
  }

  /**
   * Renders content to masthead
   *
   * @param {Object} data data to be rendered
   * @param {Object} adobeJsonObj - Adobe data from mbox
   * @returns {void}
   */
  renderContent(data, adobeJsonObj = {}) {
    let template;
    let content;
    // check returned META information
    if (data.META && data.META.CONTAINER_TYPE) {
      if (data.META.CONTAINER_TYPE.toLowerCase() === this.containerType) {
        content = data;
      } else {
        content = this.defaultContent;
        console.error(`Targeting Error: Wrong container type: ${data.META.CONTAINER_TYPE}. "${this.containerType}" expected.`);
      }
    } else {
      content = this.defaultContent;
      console.error('Targeting Error: missing Meta information');
    }

    // remove classes that may have been inadvertently added from target-preview module
    this.partialContainer.setAttribute('class', 'partial-container');
    try {
      template = _moduleConfig.partials[this.templateName];
    } catch (e) {
      console.error(e);
      template = _moduleConfig.partials[this.defaultTemplate];
    }
    if (this.usingDefaultContent) {
      this.partialContainer.setAttribute('data-default-content', this.adContainer);
    } else {
      this.partialContainer.setAttribute('data-ad-container', this.adContainer);
      if (content.clickToken) {
        this.partialContainer.setAttribute('data-ad-click-token', content.clickToken);
      }
    }
    const generatedContent = JSON.parse(Handlebars.compile(JSON.stringify(content))());
    this.jcrDefaults.videoControls = this.jcrVideoControls;
    this.partialContainer.innerHTML = template(_.defaultsDeep(generatedContent, this.jcrDefaults, this.jcrVideoControls, {
      params: this.params
    }));
    this.partialContainer.classList.add(this.templateName);
    this.settings.target.classList.add('loaded');
    this.svgFocusFix.bind(this);
    if (content.videoBackground) {
      this.renderVideo(content.videoBackground);
    }
    if (content.animations) {
      const images = this.partialContainer.querySelectorAll('img');
      let imagesLoaded = 0;
      let imagesErrored = 0;
      if (images.length) {
        [...images].forEach(image => {
          const img = image;
          img.onload = () => {
            imagesLoaded += 1;
            if (imagesLoaded + imagesErrored === images.length) {
              animationUtility.init(this.settings.target, content.animations);
            }
          };
          img.onerror = () => {
            imagesErrored += 1;
          };
        });
      } else {
        animationUtility.init(this.settings.target, content.animations);
      }
    }
    modalUtility.init(content);
    if (typeof window.fireImpressions === 'function') {
      try {
        window.fireImpressions();
      } catch (e) {
        console.error(e);
      }
    }
    if (adobeJsonObj && adobeJsonObj.coids) {
      requestAnimationFrame(() => {
        // Adobe targeting coids provided, tag the links
        if (!taggingUtility.tagLinks(adobeJsonObj.coids)) {
          console.error('Error adding tags');
        }
      });
    }

    // Rebuild any footnotes added via Shield:;
    spartaEventsUtility.pub('FOOTNOTES_REBUILD.footnoteUtility');

    // Publish event that content is rendered
    spartaEventsUtility.pub(EVENTS.SHIELD_CONTENT_RENDERED, {});
  }

  /**
   * renderVideo
   *
   * @param {Object} videoBg video content
   * @returns {void}
   */
  renderVideo(videoBg) {
    const videoBackground = new VideoBackground(this, videoBg);
    videoBackground.init();
  }

  /**
   * svgFocusFix
   *
   * @returns {void}
   */
  svgFocusFix() {
    const svgs = [].slice.call(this.settings.target.querySelectorAll(`${SELECTORS.MASTHEAD_CONTENT} svg`));
    svgs.forEach(svg => {
      if (!svg.hasAttribute('focusable')) {
        svg.setAttribute('focusable', 'false');
      }
    });
  }

  /**
   * updateSecureLinks
   *
   * @returns {void}
   */
  updateSecureLinks() {
    if (!spaParams.siteParams.env.match(/prod/)) {
      const anchorsToUpdate = SecureLinkUtility.collectLinks(this.settings.target, '[href*="https://secure.bankofamerica.com/"]');
      SecureLinkUtility.iterateLinks(spaParams.siteParams.secureHost, anchorsToUpdate);
    }
  }

  /**
   * Registers helpers required for runtime partial rendering
   *
   * @returns {void}
   * @static
   */
  static registerHelpers() {
    /* eslint-disable func-names */
    // eslint-disable-next-line consistent-return
    Handlebars.registerHelper('include', function (templateName) {
      const partial = _moduleConfig.partials[templateName];
      return new Handlebars.SafeString(partial(this));
    });
    /* eslint-enable func-names */

    Handlebars.registerHelper('isArray', function isArrayHelper(a, opts) {
      return Array.isArray(a) ? opts.fn(this) : opts.inverse(this);
    });
    Handlebars.registerHelper('isObject', function isObjectHelper(item, opts) {
      return typeof item === 'object' ? opts.fn(this) : opts.inverse(this);
    });
    Handlebars.registerHelper('json', json);
    Handlebars.registerHelper('assetUrl', assetUrl);
    Handlebars.registerHelper('barker', barker);
    Handlebars.registerHelper('layerContent', layerContent);
    Handlebars.registerHelper('cinemaHelper', cinemaHelper);
  }
}
registerModuleClass({
  moduleClass: MastheadContainer,
  moduleConfig: _moduleConfig
});
const moduleConfig = _moduleConfig;

export { MastheadContainer as default, moduleConfig };
